import { DateTime } from 'ts-luxon';

import { asLabel } from '@shure/shared/angular/utils/i18n';

import { CountriesList, TimeZone } from './models';

export interface CountryDefinition {
	id: string;
	label: string;
	dateFormat?: string;
}

export interface TimeZoneRaw {
	id: string;
	timezone: string;
}

function processTimezone(tz: TimeZoneRaw, timezonesArray: TimeZone[]): TimeZone {
	const offsetMinutes = DateTime.now().setZone(tz.id).offset;
	const hours = Math.floor(Math.abs(offsetMinutes) / 60);
	const minutes = Math.abs(offsetMinutes) % 60;
	const sign = offsetMinutes >= 0 ? '+' : '-';
	const formattedOffset = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
	const gmtOffset = `GMT${formattedOffset}`;

	const timezoneObj: TimeZone = {
		id: tz.id,
		timezone: tz.timezone,
		gmtOffset
	};

	timezonesArray.push(timezoneObj);

	return timezoneObj;
}

/**
 * Updates a list of countries with translated labels and timezone offsets.
 * Processes each country's timezones, formats their GMT offset, and builds a unique list of timezones.
 *
 * @returns An object containing:
 *  - countries: A record mapping isoCode to updated country objects with translated labels and formatted timezones.
 *  - timezones: An array of unique timezone objects with id, gmtoffset and translated label.
 */
export function updateCountriesWithOffset(): {
	countries: CountriesList;
	timezones: TimeZone[];
	// eslint-disable-next-line prettier/prettier
	} {
	const updatedCountries: CountriesList = {};
	const timezonesArray: TimeZone[] = [];

	for (const country of COUNTRIES_AND_TIMEZONES) {
		updatedCountries[country.isoCode] = {
			id: country.id,
			isoCode: country.isoCode,
			label: country.label,
			dateFormat: country.dateFormat,
			timezones: country.timezones.map((tz) => processTimezone(tz, timezonesArray))
		};
	}

	return { countries: updatedCountries, timezones: timezonesArray };
}

/**
 * Sorts an array of time zone objects based on their GMT offset in ascending order.
 * Each time zone object is expected to have a `gmtOffset` property in the format `GMT±HH:MM`.
 * If the `gmtOffset` is missing or invalid, it defaults to `GMT+00:00` (UTC).
 * @param timeZones - An array of time zone objects to be sorted.
 * @returns A new array of time zone objects sorted by their GMT offset.
 */
export function sortTimeZonesByOffset(timeZones: TimeZone[]): TimeZone[] {
	const offsetToMinutes = (offset: string): number => {
		const match = offset.match(/GMT([+-])(\d{2}):(\d{2})/);
		if (!match) return 0;
		const [, sign, hrs, mins] = match;
		const total = parseInt(hrs) * 60 + parseInt(mins);
		return sign === '+' ? total : -total;
	};

	return timeZones.sort(
		(a, b) => offsetToMinutes(a.gmtOffset ?? 'GMT+00:00') - offsetToMinutes(b.gmtOffset ?? 'GMT+00:00')
	);
}

export const SUPPORTED_COUNTRIES: CountryDefinition[] = [
	{ id: 'Afghanistan', label: 'Afghanistan' },
	{ id: 'Aland Islands', label: 'Aland Islands' },
	{ id: 'Albania', label: 'Albania' },
	{ id: 'Algeria', label: 'Algeria' },
	{ id: 'American Virgin Islands', label: 'U.S. Virgin Islands' },
	{ id: 'Andorra', label: 'Andorra' },
	{ id: 'Angola', label: 'Angola' },
	{ id: 'Anguilla', label: 'Anguilla' },
	{ id: 'Antarctica', label: 'Antarctica' },
	{ id: 'Antigua and Barbuda', label: 'Antigua and Barbuda' },
	{ id: 'Argentina', label: 'Argentina' },
	{ id: 'Armenia', label: 'Armenia' },
	{ id: 'Aruba', label: 'Aruba' },
	{ id: 'Australia', label: 'Australia' },
	{ id: 'Austria', label: 'Austria' },
	{ id: 'Azerbaijan', label: 'Azerbaijan' },
	{ id: 'Bahamas', label: 'Bahamas' },
	{ id: 'Bahrain', label: 'Bahrain' },
	{ id: 'Bangladesh', label: 'Bangladesh' },
	{ id: 'Barbados', label: 'Barbados' },
	{ id: 'Belgium', label: 'Belgium' },
	{ id: 'Belize', label: 'Belize' },
	{ id: 'Benin', label: 'Benin' },
	{ id: 'Bermuda', label: 'Bermuda' },
	{ id: 'Bhutan', label: 'Bhutan' },
	{ id: 'Bolivia, Plurinational State of', label: 'Bolivia' },
	{ id: 'Bonaire, Sint Eustatius and Saba', label: 'Bonaire, Sint Eustatius and Saba' },
	{ id: 'Bosnia and Herzegovina', label: 'Bosnia and Herzegovina' },
	{ id: 'Botswana', label: 'Botswana' },
	{ id: 'Bouvet Island', label: 'Bouvet Island' },
	{ id: 'Br West Indies', label: 'Br West Indies' },
	{ id: 'Brazil', label: 'Brazil' },
	{ id: 'British Indian Ocean Territory', label: 'British Indian Ocean Territory' },
	{ id: 'Brunei Darussalam', label: 'Brunei Darussalam' },
	{ id: 'Bulgaria', label: 'Bulgaria' },
	{ id: 'Burkina Faso', label: 'Burkina Faso' },
	{ id: 'Burundi', label: 'Burundi' },
	{ id: 'Cambodia', label: 'Cambodia' },
	{ id: 'Cameroon', label: 'Cameroon' },
	{ id: 'Canada', label: 'Canada' },
	{ id: 'Cape Verde', label: 'Cape Verde' },
	{ id: 'Cayman Islands', label: 'Cayman Islands' },
	{ id: 'Central African Republic', label: 'Central African Republic' },
	{ id: 'Chad', label: 'Chad' },
	{ id: 'Chile', label: 'Chile' },
	{ id: 'China', label: 'China' },
	{ id: 'Chinese Taipei', label: 'Chinese Taipei' },
	{ id: 'Christmas Island', label: 'Christmas Island' },
	{ id: 'Cocos (Keeling) Islands', label: 'Cocos (Keeling) Islands' },
	{ id: 'Colombia', label: 'Colombia' },
	{ id: 'Comoros', label: 'Comoros' },
	{ id: 'Congo', label: 'Congo' },
	{ id: 'Congo, the Democratic Republic of the', label: 'Democratic Republic of the Congo' },
	{ id: 'Cook Islands', label: 'Cook Islands' },
	{ id: 'Costa Rica', label: 'Costa Rica' },
	{ id: 'Cote d’Ivoire', label: 'Cote d’Ivoire' },
	{ id: 'Croatia', label: 'Croatia' },
	{ id: 'Curaçao', label: 'Curaçao' },
	{ id: 'Cyprus', label: 'Cyprus' },
	{ id: 'Czech Republic', label: 'Czech Republic' },
	{ id: 'Denmark', label: 'Denmark' },
	{ id: 'Djibouti', label: 'Djibouti' },
	{ id: 'Dominica', label: 'Dominica' },
	{ id: 'Dominican Republic', label: 'Dominican Republic' },
	{ id: 'Dutch Antilles', label: 'Dutch Antilles' },
	{ id: 'Ecuador', label: 'Ecuador' },
	{ id: 'Egypt', label: 'Egypt' },
	{ id: 'El Salvador', label: 'El Salvador' },
	{ id: 'England', label: 'England' },
	{ id: 'Equatorial Guinea', label: 'Equatorial Guinea' },
	{ id: 'Eritrea', label: 'Eritrea' },
	{ id: 'Estonia', label: 'Estonia' },
	{ id: 'Ethiopia', label: 'Ethiopia' },
	{ id: 'Falkland Islands (Malvinas)', label: 'Falkland Islands' },
	{ id: 'Faroe Islands', label: 'Faroe Islands' },
	{ id: 'Fiji', label: 'Fiji' },
	{ id: 'Finland', label: 'Finland' },
	{ id: 'France', label: 'France' },
	{ id: 'French Guiana', label: 'French Guiana' },
	{ id: 'French Polynesia', label: 'French Polynesia' },
	{ id: 'French Southern Territories', label: 'French Southern Territories' },
	{ id: 'Gabon', label: 'Gabon' },
	{ id: 'Gambia', label: 'Gambia' },
	{ id: 'Georgia', label: 'Georgia' },
	{ id: 'Germany', label: 'Germany' },
	{ id: 'Ghana', label: 'Ghana' },
	{ id: 'Gibraltar', label: 'Gibraltar' },
	{ id: 'Greece', label: 'Greece' },
	{ id: 'Greenland', label: 'Greenland' },
	{ id: 'Grenada', label: 'Grenada' },
	{ id: 'Guadeloupe', label: 'Guadeloupe' },
	{ id: 'Guam', label: 'Guam' },
	{ id: 'Guatemala', label: 'Guatemala' },
	{ id: 'Guernsey', label: 'Guernsey' },
	{ id: 'Guinea', label: 'Guinea' },
	{ id: 'Guinea-Bissau', label: 'Guinea-Bissau' },
	{ id: 'Guyana', label: 'Guyana' },
	{ id: 'Haiti', label: 'Haiti' },
	{ id: 'Heard Island and McDonald Islands', label: 'Heard Island and McDonald Islands' },
	{ id: 'Holy See (Vatican City State)', label: 'Holy See (Vatican City State)' },
	{ id: 'Honduras', label: 'Honduras' },
	{ id: 'Hong Kong S.A.R.', label: 'Hong Kong S.A.R.' },
	{ id: 'Hungary', label: 'Hungary' },
	{ id: 'Iceland', label: 'Iceland' },
	{ id: 'India', label: 'India' },
	{ id: 'Indonesia', label: 'Indonesia' },
	{ id: 'Iraq', label: 'Iraq' },
	{ id: 'Ireland', label: 'Ireland' },
	{ id: 'Isle of Man', label: 'Isle of Man' },
	{ id: 'Israel', label: 'Israel' },
	{ id: 'Italy', label: 'Italy' },
	{ id: 'Jamaica', label: 'Jamaica' },
	{ id: 'Japan', label: 'Japan' },
	{ id: 'Jersey', label: 'Jersey' },
	{ id: 'Jordan', label: 'Jordan' },
	{ id: 'Kazakhstan', label: 'Kazakhstan' },
	{ id: 'Kenya', label: 'Kenya' },
	{ id: 'Kiribati', label: 'Kiribati' },
	{ id: 'Korea, Republic of', label: 'South Korea' },
	{ id: 'Kuwait', label: 'Kuwait' },
	{ id: 'Kyrgyzstan', label: 'Kyrgyzstan' },
	{ id: 'Lao Peoples Democratic Republic', label: 'Laos' },
	{ id: 'Latvia', label: 'Latvia' },
	{ id: 'Lebanon', label: 'Lebanon' },
	{ id: 'Lesotho', label: 'Lesotho' },
	{ id: 'Liberia', label: 'Liberia' },
	{ id: 'Libyan Arab Jamahiriya', label: 'Libyan Arab Jamahiriya' },
	{ id: 'Liechtenstein', label: 'Liechtenstein' },
	{ id: 'Lithuania', label: 'Lithuania' },
	{ id: 'Luxembourg', label: 'Luxembourg' },
	{ id: 'Macao', label: 'Macao S.A.R., China' },
	{ id: 'Macedonia, the former Yugoslav Republic of', label: 'Macedonia' },
	{ id: 'Madagascar', label: 'Madagascar' },
	{ id: 'Malawi', label: 'Malawi' },
	{ id: 'Malaysia', label: 'Malaysia' },
	{ id: 'Maldives', label: 'Maldives' },
	{ id: 'Mali', label: 'Mali' },
	{ id: 'Malta', label: 'Malta' },
	{ id: 'Martinique', label: 'Martinique' },
	{ id: 'Mauritania', label: 'Mauritania' },
	{ id: 'Mauritius', label: 'Mauritius' },
	{ id: 'Mayotte', label: 'Mayotte' },
	{ id: 'Mexico', label: 'Mexico' },
	{ id: 'Moldova, Republic of', label: 'Moldova' },
	{ id: 'Monaco', label: 'Monaco' },
	{ id: 'Mongolia', label: 'Mongolia' },
	{ id: 'Montenegro', label: 'Montenegro' },
	{ id: 'Montserrat', label: 'Montserrat' },
	{ id: 'Morocco', label: 'Morocco' },
	{ id: 'Mozambique', label: 'Mozambique' },
	{ id: 'Myanmar', label: 'Myanmar' },
	{ id: 'N. Mariana Island', label: 'N. Mariana Island' },
	{ id: 'Namibia', label: 'Namibia' },
	{ id: 'Nauru', label: 'Nauru' },
	{ id: 'Nepal', label: 'Nepal' },
	{ id: 'Netherlands', label: 'Netherlands' },
	{ id: 'New Caledonia', label: 'New Caledonia' },
	{ id: 'New Zealand', label: 'New Zealand' },
	{ id: 'Nicaragua', label: 'Nicaragua' },
	{ id: 'Niger', label: 'Niger' },
	{ id: 'Nigeria', label: 'Nigeria' },
	{ id: 'Niue', label: 'Niue' },
	{ id: 'Norfolk Island', label: 'Norfolk Island' },
	{ id: 'Norway', label: 'Norway' },
	{ id: 'Oman', label: 'Oman' },
	{ id: 'Pakistan', label: 'Pakistan' },
	{ id: 'Palestinian Territory, Occupied', label: 'Palestinian Territory' },
	{ id: 'Panama', label: 'Panama' },
	{ id: 'Papua New Guinea', label: 'Papua New Guinea' },
	{ id: 'Paraguay', label: 'Paraguay' },
	{ id: 'Peru', label: 'Peru' },
	{ id: 'Philippines', label: 'Philippines' },
	{ id: 'Pitcairn', label: 'Pitcairn' },
	{ id: 'Poland', label: 'Poland' },
	{ id: 'Portugal', label: 'Portugal' },
	{ id: 'Puerto Rico', label: 'Puerto Rico' },
	{ id: 'Qatar', label: 'Qatar' },
	{ id: 'Reunion', label: 'Reunion' },
	{ id: 'Romania', label: 'Romania' },
	{ id: 'Rwanda', label: 'Rwanda' },
	{ id: 'Saint Barthélemy', label: 'Saint Barthélemy' },
	{ id: 'Saint Helena, Ascension and Tristan da Cunha', label: 'Saint Helena, Ascension and Tristan da Cunha' },
	{ id: 'Saint Kitts and Nevis', label: 'Saint Kitts and Nevis' },
	{ id: 'Saint Lucia', label: 'Saint Lucia' },
	{ id: 'Saint Martin (French part)', label: 'Saint Martin (French part)' },
	{ id: 'Saint Pierre and Miquelon', label: 'Saint Pierre and Miquelon' },
	{ id: 'Saint Vincent and the Grenadines', label: 'Saint Vincent and the Grenadines' },
	{ id: 'Samoa', label: 'Samoa' },
	{ id: 'San Marino', label: 'San Marino' },
	{ id: 'Sao Tome and Principe', label: 'Sao Tome and Principe' },
	{ id: 'Saudi Arabia', label: 'Saudi Arabia' },
	{ id: 'Senegal', label: 'Senegal' },
	{ id: 'Serbia', label: 'Serbia' },
	{ id: 'Seychelles', label: 'Seychelles' },
	{ id: 'Sierra Leone', label: 'Sierra Leone' },
	{ id: 'Singapore', label: 'Singapore' },
	{ id: 'Sint Maarten (Dutch part)', label: 'Sint Maarten (Dutch part)' },
	{ id: 'Slovakia', label: 'Slovakia' },
	{ id: 'Slovenia', label: 'Slovenia' },
	{ id: 'Solomon Islands', label: 'Solomon Islands' },
	{ id: 'Somalia', label: 'Somalia' },
	{ id: 'South Africa', label: 'South Africa' },
	{ id: 'South Georgia and the South Sandwich Islands', label: 'South Georgia and the South Sandwich Islands' },
	{ id: 'South Sudan', label: 'South Sudan' },
	{ id: 'Spain', label: 'Spain' },
	{ id: 'Sri Lanka', label: 'Sri Lanka' },
	{ id: 'Suriname', label: 'Suriname' },
	{ id: 'Svalbard and Jan Mayen', label: 'Svalbard and Jan Mayen' },
	{ id: 'Swaziland', label: 'Swaziland' },
	{ id: 'Sweden', label: 'Sweden' },
	{ id: 'Switzerland', label: 'Switzerland' },
	{ id: 'Tajikistan', label: 'Tajikistan' },
	{ id: 'Tanzania, United Republic of', label: 'Tanzania' },
	{ id: 'Thailand', label: 'Thailand' },
	{ id: 'Timor-Leste', label: 'Timor-Leste' },
	{ id: 'Togo', label: 'Togo' },
	{ id: 'Tokelau', label: 'Tokelau' },
	{ id: 'Tonga', label: 'Tonga' },
	{ id: 'Trinidad and Tobago', label: 'Trinidad and Tobago' },
	{ id: 'Tunisia', label: 'Tunisia' },
	{ id: 'Turkey', label: 'Turkey' },
	{ id: 'Turkmenistan', label: 'Turkmenistan' },
	{ id: 'Turks and Caicos Islands', label: 'Turks and Caicos Islands' },
	{ id: 'Tuvalu', label: 'Tuvalu' },
	{ id: 'Uganda', label: 'Uganda' },
	{ id: 'United Arab Emirates', label: 'United Arab Emirates' },
	{ id: 'United Kingdom', label: 'United Kingdom' },
	{ id: 'United States', label: 'United States' },
	{ id: 'Uruguay', label: 'Uruguay' },
	{ id: 'Uzbekistan', label: 'Uzbekistan' },
	{ id: 'Vanuatu', label: 'Vanuatu' },
	{ id: 'Venezuela, Bolivarian Republic of', label: 'Venezuela, Bolivarian Republic of' },
	{ id: 'Viet Nam', label: 'Vietnam' },
	{ id: 'Virgin Islands, British', label: 'British Virgin Islands' },
	{ id: 'Wallis and Futuna', label: 'Wallis and Futuna' },
	{ id: 'Western Sahara', label: 'Western Sahara' },
	{ id: 'Yemen', label: 'Yemen' },
	{ id: 'Zambia', label: 'Zambia' },
	{ id: 'Zimbabwe', label: 'Zimbabwe' }
];

//TODO: MM - Confirm why supported countries is different for My Account and Create Org
export const SUPPORTED_COUNTRIES_MY_ACCOUNT: CountryDefinition[] = [
	{
		id: 'Afghanistan',
		label: asLabel('countries-list.afghanistan')
	},
	{
		id: 'Aland Islands',
		label: asLabel('countries-list.aland-islands')
	},
	{
		id: 'Albania',
		label: asLabel('countries-list.albania'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Algeria',
		label: asLabel('countries-list.algeria')
	},
	{
		id: 'American Virgin Islands',
		label: asLabel('countries-list.u.s.-virgin-islands')
	},
	{
		id: 'Andorra',
		label: asLabel('countries-list.andorra')
	},
	{
		id: 'Angola',
		label: asLabel('countries-list.angola')
	},
	{
		id: 'Anguilla',
		label: asLabel('countries-list.anguilla')
	},
	{
		id: 'Antarctica',
		label: asLabel('countries-list.antarctica')
	},
	{
		id: 'Antigua and Barbuda',
		label: asLabel('countries-list.antigua-and-barbuda')
	},
	{
		id: 'Argentina',
		label: asLabel('countries-list.argentina')
	},
	{
		id: 'Armenia',
		label: asLabel('countries-list.armenia'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Aruba',
		label: asLabel('countries-list.aruba')
	},
	{
		id: 'Australia',
		label: asLabel('countries-list.australia'),
		dateFormat: 'dd-mm-yyyy'
	},
	{
		id: 'Austria',
		label: asLabel('countries-list.austria'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Azerbaijan',
		label: asLabel('countries-list.azerbaijan'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Bahamas',
		label: asLabel('countries-list.bahamas')
	},
	{
		id: 'Bahrain',
		label: asLabel('countries-list.bahrain')
	},
	{
		id: 'Bangladesh',
		label: asLabel('countries-list.bangladesh'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Barbados',
		label: asLabel('countries-list.barbados')
	},
	{
		id: 'Belgium',
		label: asLabel('countries-list.belgium'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Belize',
		label: asLabel('countries-list.belize')
	},
	{
		id: 'Benin',
		label: asLabel('countries-list.benin')
	},
	{
		id: 'Bermuda',
		label: asLabel('countries-list.bermuda')
	},
	{
		id: 'Bhutan',
		label: asLabel('countries-list.bhutan')
	},
	{
		id: 'Bolivia, Plurinational State of',
		label: asLabel('countries-list.bolivia')
	},
	{
		id: 'Bonaire, Sint Eustatius and Saba',
		label: asLabel('countries-list.bonaire,-sint-eustatius-and-saba')
	},
	{
		id: 'Bosnia and Herzegovina',
		label: asLabel('countries-list.bosnia-and-herzegovina')
	},
	{
		id: 'Botswana',
		label: asLabel('countries-list.botswana')
	},
	{
		id: 'Bouvet Island',
		label: asLabel('countries-list.bouvet-island')
	},
	{
		id: 'Br West Indies',
		label: asLabel('countries-list.br-west-indies')
	},
	{
		id: 'Brazil',
		label: asLabel('countries-list.brazil'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'British Indian Ocean Territory',
		label: asLabel('countries-list.british-indian-ocean-territory')
	},
	{
		id: 'Brunei Darussalam',
		label: asLabel('countries-list.brunei-darussalam')
	},
	{
		id: 'Bulgaria',
		label: asLabel('countries-list.bulgaria'),
		dateFormat: 'dd.mm.yyyy'
	},
	{
		id: 'Burkina Faso',
		label: asLabel('countries-list.burkina-faso')
	},
	{
		id: 'Burundi',
		label: asLabel('countries-list.burundi')
	},
	{
		id: 'Cambodia',
		label: asLabel('countries-list.cambodia'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Cameroon',
		label: asLabel('countries-list.cameroon')
	},
	{
		id: 'Canada',
		label: asLabel('countries-list.canada'),
		dateFormat: 'mm-dd-yyyy'
	},
	{
		id: 'Cape Verde',
		label: asLabel('countries-list.cape-verde')
	},
	{
		id: 'Cayman Islands',
		label: asLabel('countries-list.cayman-islands')
	},
	{
		id: 'Central African Republic',
		label: asLabel('countries-list.central-african-republic')
	},
	{
		id: 'Chad',
		label: asLabel('countries-list.chad')
	},
	{
		id: 'Chile',
		label: asLabel('countries-list.chile')
	},
	{
		id: 'China',
		label: asLabel('countries-list.china'),
		dateFormat: 'mm/dd/yyyy'
	},
	{
		id: 'Chinese Taipei',
		label: asLabel('countries-list.chinese-taipei'),
		dateFormat: 'mm-dd-yyyy'
	},
	{
		id: 'Christmas Island',
		label: asLabel('countries-list.christmas-island')
	},
	{
		id: 'Cocos (Keeling) Islands',
		label: asLabel('countries-list.cocos-(keeling)-islands')
	},
	{
		id: 'Colombia',
		label: asLabel('countries-list.colombia')
	},
	{
		id: 'Comoros',
		label: asLabel('countries-list.comoros')
	},
	{
		id: 'Congo',
		label: asLabel('countries-list.congo')
	},
	{
		id: 'Congo, the Democratic Republic of the',
		label: asLabel('countries-list.democratic-republic-of-the-congo')
	},
	{
		id: 'Cook Islands',
		label: asLabel('countries-list.cook-islands')
	},
	{
		id: 'Costa Rica',
		label: asLabel('countries-list.costa-rica')
	},
	{
		id: 'Cote d’Ivoire',
		label: asLabel('countries-list.cote-divoire')
	},
	{
		id: 'Croatia',
		label: asLabel('countries-list.croatia'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Curaçao',
		label: asLabel('countries-list.curaçao')
	},
	{
		id: 'Cyprus',
		label: asLabel('countries-list.cyprus')
	},
	{
		id: 'Czech Republic',
		label: asLabel('countries-list.czech-republic'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Denmark',
		label: asLabel('countries-list.denmark'),
		dateFormat: 'dd-mm-yyyy'
	},
	{
		id: 'Djibouti',
		label: asLabel('countries-list.djibouti')
	},
	{
		id: 'Dominica',
		label: asLabel('countries-list.dominica')
	},
	{
		id: 'Dominican Republic',
		label: asLabel('countries-list.dominican-republic')
	},
	{
		id: 'Dutch Antilles',
		label: asLabel('countries-list.dutch-antilles')
	},
	{
		id: 'Ecuador',
		label: asLabel('countries-list.ecuador')
	},
	{
		id: 'Egypt',
		label: asLabel('countries-list.egypt')
	},
	{
		id: 'El Salvador',
		label: asLabel('countries-list.el-salvador')
	},
	{
		id: 'England',
		label: asLabel('countries-list.england')
	},
	{
		id: 'Equatorial Guinea',
		label: asLabel('countries-list.equatorial-guinea')
	},
	{
		id: 'Eritrea',
		label: asLabel('countries-list.eritrea')
	},
	{
		id: 'Estonia',
		label: asLabel('countries-list.estonia'),
		dateFormat: 'dd-mm-yyyy'
	},
	{
		id: 'Ethiopia',
		label: asLabel('countries-list.ethiopia'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Falkland Islands (Malvinas)',
		label: asLabel('countries-list.falkland-islands')
	},
	{
		id: 'Faroe Islands',
		label: asLabel('countries-list.faroe-islands')
	},
	{
		id: 'Fiji',
		label: asLabel('countries-list.fiji')
	},
	{
		id: 'Finland',
		label: asLabel('countries-list.finland'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'France',
		label: asLabel('countries-list.france'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'French Guiana',
		label: asLabel('countries-list.french-guiana')
	},
	{
		id: 'French Polynesia',
		label: asLabel('countries-list.french-polynesia')
	},
	{
		id: 'French Southern Territories',
		label: asLabel('countries-list.french-southern-territories')
	},
	{
		id: 'Gabon',
		label: asLabel('countries-list.gabon')
	},
	{
		id: 'Gambia',
		label: asLabel('countries-list.gambia')
	},
	{
		id: 'Georgia',
		label: asLabel('countries-list.georgia'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Germany',
		label: asLabel('countries-list.germany'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Ghana',
		label: asLabel('countries-list.ghana')
	},
	{
		id: 'Gibraltar',
		label: asLabel('countries-list.gibraltar')
	},
	{
		id: 'Greece',
		label: asLabel('countries-list.greece'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Greenland',
		label: asLabel('countries-list.greenland')
	},
	{
		id: 'Grenada',
		label: asLabel('countries-list.grenada')
	},
	{
		id: 'Guadeloupe',
		label: asLabel('countries-list.guadeloupe')
	},
	{
		id: 'Guam',
		label: asLabel('countries-list.guam')
	},
	{
		id: 'Guatemala',
		label: asLabel('countries-list.guatemala')
	},
	{
		id: 'Guernsey',
		label: asLabel('countries-list.guernsey')
	},
	{
		id: 'Guinea',
		label: asLabel('countries-list.guinea')
	},
	{
		id: 'Guinea-Bissau',
		label: asLabel('countries-list.guinea-bissau')
	},
	{
		id: 'Guyana',
		label: asLabel('countries-list.guyana')
	},
	{
		id: 'Haiti',
		label: asLabel('countries-list.haiti')
	},
	{
		id: 'Heard Island and McDonald Islands',
		label: asLabel('countries-list.heard-island-and-mcdonald-islands')
	},
	{
		id: 'Holy See (Vatican City State)',
		label: asLabel('countries-list.holy-see-(vatican-city-state)')
	},
	{
		id: 'Honduras',
		label: asLabel('countries-list.honduras')
	},
	{
		id: 'Hong Kong S.A.R.',
		label: asLabel('countries-list.hong-kong-s.a.r.'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Hungary',
		label: asLabel('countries-list.hungary'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Iceland',
		label: asLabel('countries-list.iceland'),
		dateFormat: 'dd-mm-yyyy'
	},
	{
		id: 'India',
		label: asLabel('countries-list.india'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Indonesia',
		label: asLabel('countries-list.indonesia'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Iraq',
		label: asLabel('countries-list.iraq')
	},
	{
		id: 'Ireland',
		label: asLabel('countries-list.ireland'),
		dateFormat: 'mm-dd-yyyy'
	},
	{
		id: 'Isle of Man',
		label: asLabel('countries-list.isle-of-man')
	},
	{
		id: 'Israel',
		label: asLabel('countries-list.israel'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Italy',
		label: asLabel('countries-list.italy'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Jamaica',
		label: asLabel('countries-list.jamaica')
	},
	{
		id: 'Japan',
		label: asLabel('countries-list.japan'),
		dateFormat: 'mm/dd/yyyy'
	},
	{
		id: 'Jersey',
		label: asLabel('countries-list.jersey')
	},
	{
		id: 'Jordan',
		label: asLabel('countries-list.jordan')
	},
	{
		id: 'Kazakhstan',
		label: asLabel('countries-list.kazakhstan'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Kenya',
		label: asLabel('countries-list.kenya'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Kiribati',
		label: asLabel('countries-list.kiribati')
	},
	{
		id: 'Korea, Republic of',
		label: asLabel('countries-list.south-korea'),
		dateFormat: 'mm/dd/yyyy'
	},
	{
		id: 'Kuwait',
		label: asLabel('countries-list.kuwait')
	},
	{
		id: 'Kyrgyzstan',
		label: asLabel('countries-list.kyrgyzstan')
	},
	{
		id: 'Lao Peoples Democratic Republic',
		label: asLabel('countries-list.laos')
	},
	{
		id: 'Latvia',
		label: asLabel('countries-list.latvia'),
		dateFormat: 'yyyy.dd.mm'
	},
	{
		id: 'Lebanon',
		label: asLabel('countries-list.lebanon')
	},
	{
		id: 'Lesotho',
		label: asLabel('countries-list.lesotho')
	},
	{
		id: 'Liberia',
		label: asLabel('countries-list.liberia')
	},
	{
		id: 'Libyan Arab Jamahiriya',
		label: asLabel('countries-list.libyan-arab-jamahiriya')
	},
	{
		id: 'Liechtenstein',
		label: asLabel('countries-list.liechtenstein')
	},
	{
		id: 'Lithuania',
		label: asLabel('countries-list.lithuania'),
		dateFormat: 'yyyy.mm.dd'
	},
	{
		id: 'Luxembourg',
		label: asLabel('countries-list.luxembourg')
	},
	{
		id: 'Macao',
		label: asLabel('countries-list.macao-s.a.r.,-china')
	},
	{
		id: 'Macedonia, the former Yugoslav Republic of',
		label: asLabel('countries-list.macedonia')
	},
	{
		id: 'Madagascar',
		label: asLabel('countries-list.madagascar')
	},
	{
		id: 'Malawi',
		label: asLabel('countries-list.malawi')
	},
	{
		id: 'Malaysia',
		label: asLabel('countries-list.malaysia'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Maldives',
		label: asLabel('countries-list.maldives')
	},
	{
		id: 'Mali',
		label: asLabel('countries-list.mali')
	},
	{
		id: 'Malta',
		label: asLabel('countries-list.malta'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Martinique',
		label: asLabel('countries-list.martinique')
	},
	{
		id: 'Mauritania',
		label: asLabel('countries-list.mauritania')
	},
	{
		id: 'Mauritius',
		label: asLabel('countries-list.mauritius')
	},
	{
		id: 'Mayotte',
		label: asLabel('countries-list.mayotte')
	},
	{
		id: 'Mexico',
		label: asLabel('countries-list.mexico'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Moldova, Republic of',
		label: asLabel('countries-list.moldova')
	},
	{
		id: 'Monaco',
		label: asLabel('countries-list.monaco')
	},
	{
		id: 'Mongolia',
		label: asLabel('countries-list.mongolia')
	},
	{
		id: 'Montenegro',
		label: asLabel('countries-list.montenegro'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Montserrat',
		label: asLabel('countries-list.montserrat')
	},
	{
		id: 'Morocco',
		label: asLabel('countries-list.morocco')
	},
	{
		id: 'Mozambique',
		label: asLabel('countries-list.mozambique')
	},
	{
		id: 'Myanmar',
		label: asLabel('countries-list.myanmar')
	},
	{
		id: 'N. Mariana Island',
		label: asLabel('countries-list.n.-mariana-island')
	},
	{
		id: 'Namibia',
		label: asLabel('countries-list.namibia')
	},
	{
		id: 'Nauru',
		label: asLabel('countries-list.nauru')
	},
	{
		id: 'Nepal',
		label: asLabel('countries-list.nepal'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Netherlands',
		label: asLabel('countries-list.netherlands'),
		dateFormat: 'dd-mm-yyyy'
	},
	{
		id: 'New Caledonia',
		label: asLabel('countries-list.new-caledonia')
	},
	{
		id: 'New Zealand',
		label: asLabel('countries-list.new-zealand'),
		dateFormat: 'dd-mm-yyyy'
	},
	{
		id: 'Nicaragua',
		label: asLabel('countries-list.nicaragua')
	},
	{
		id: 'Niger',
		label: asLabel('countries-list.niger')
	},
	{
		id: 'Nigeria',
		label: asLabel('countries-list.nigeria'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Niue',
		label: asLabel('countries-list.niue')
	},
	{
		id: 'Norfolk Island',
		label: asLabel('countries-list.norfolk-island')
	},
	{
		id: 'Norway',
		label: asLabel('countries-list.norway'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Oman',
		label: asLabel('countries-list.oman')
	},
	{
		id: 'Pakistan',
		label: asLabel('countries-list.pakistan'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Palestinian Territory, Occupied',
		label: asLabel('countries-list.palestinian-territory')
	},
	{
		id: 'Panama',
		label: asLabel('countries-list.panama')
	},
	{
		id: 'Papua New Guinea',
		label: asLabel('countries-list.papua-new-guinea')
	},
	{
		id: 'Paraguay',
		label: asLabel('countries-list.paraguay')
	},
	{
		id: 'Peru',
		label: asLabel('countries-list.peru')
	},
	{
		id: 'Philippines',
		label: asLabel('countries-list.philippines'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Pitcairn',
		label: asLabel('countries-list.pitcairn')
	},
	{
		id: 'Poland',
		label: asLabel('countries-list.poland'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Portugal',
		label: asLabel('countries-list.portugal'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Puerto Rico',
		label: asLabel('countries-list.puerto-rico')
	},
	{
		id: 'Qatar',
		label: asLabel('countries-list.qatar')
	},
	{
		id: 'Reunion',
		label: asLabel('countries-list.reunion')
	},
	{
		id: 'Romania',
		label: asLabel('countries-list.romania'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Rwanda',
		label: asLabel('countries-list.rwanda'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Saint Barthélemy',
		label: asLabel('countries-list.saint-barthélemy')
	},
	{
		id: 'Saint Helena, Ascension and Tristan da Cunha',
		label: asLabel('countries-list.saint-helena,-ascension-and-tristan-da-cunha')
	},
	{
		id: 'Saint Kitts and Nevis',
		label: asLabel('countries-list.saint-kitts-and-nevis')
	},
	{
		id: 'Saint Lucia',
		label: asLabel('countries-list.saint-lucia')
	},
	{
		id: 'Saint Martin (French part)',
		label: asLabel('countries-list.saint-martin-(french-part)')
	},
	{
		id: 'Saint Pierre and Miquelon',
		label: asLabel('countries-list.saint-pierre-and-miquelon')
	},
	{
		id: 'Saint Vincent and the Grenadines',
		label: asLabel('countries-list.saint-vincent-and-the-grenadines')
	},
	{
		id: 'Samoa',
		label: asLabel('countries-list.samoa')
	},
	{
		id: 'San Marino',
		label: asLabel('countries-list.san-marino')
	},
	{
		id: 'Sao Tome and Principe',
		label: asLabel('countries-list.sao-tome-and-principe')
	},
	{
		id: 'Saudi Arabia',
		label: asLabel('countries-list.saudi-arabia')
	},
	{
		id: 'Senegal',
		label: asLabel('countries-list.senegal'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Serbia',
		label: asLabel('countries-list.serbia'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Seychelles',
		label: asLabel('countries-list.seychelles')
	},
	{
		id: 'Sierra Leone',
		label: asLabel('countries-list.sierra-leone')
	},
	{
		id: 'Singapore',
		label: asLabel('countries-list.singapore')
	},
	{
		id: 'Sint Maarten (Dutch part)',
		label: asLabel('countries-list.sint-maarten-(dutch-part)')
	},
	{
		id: 'Slovakia',
		label: asLabel('countries-list.slovakia'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Slovenia',
		label: asLabel('countries-list.slovenia'),
		dateFormat: 'dd-mm-yyyy'
	},
	{
		id: 'Solomon Islands',
		label: asLabel('countries-list.solomon-islands')
	},
	{
		id: 'Somalia',
		label: asLabel('countries-list.somalia')
	},
	{
		id: 'South Africa',
		label: asLabel('countries-list.south-africa'),
		dateFormat: 'mm-dd-yyyy'
	},
	{
		id: 'South Georgia and the South Sandwich Islands',
		label: asLabel('countries-list.south-georgia-and-the-south-sandwich-islands')
	},
	{
		id: 'South Sudan',
		label: asLabel('countries-list.south-sudan')
	},
	{
		id: 'Spain',
		label: asLabel('countries-list.spain'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Sri Lanka',
		label: asLabel('countries-list.sri-lanka'),
		dateFormat: 'dd-mm-yyyy'
	},
	{
		id: 'Suriname',
		label: asLabel('countries-list.suriname')
	},
	{
		id: 'Svalbard and Jan Mayen',
		label: asLabel('countries-list.svalbard-and-jan-mayen')
	},
	{
		id: 'Swaziland',
		label: asLabel('countries-list.swaziland')
	},
	{
		id: 'Sweden',
		label: asLabel('countries-list.sweden'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Switzerland',
		label: asLabel('countries-list.switzerland'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Tajikistan',
		label: asLabel('countries-list.tajikistan')
	},
	{
		id: 'Tanzania, United Republic of',
		label: asLabel('countries-list.tanzania'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Thailand',
		label: asLabel('countries-list.thailand'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Timor-Leste',
		label: asLabel('countries-list.timor-leste')
	},
	{
		id: 'Togo',
		label: asLabel('countries-list.togo')
	},
	{
		id: 'Tokelau',
		label: asLabel('countries-list.tokelau')
	},
	{
		id: 'Tonga',
		label: asLabel('countries-list.tonga')
	},
	{
		id: 'Trinidad and Tobago',
		label: asLabel('countries-list.trinidad-and-tobago')
	},
	{
		id: 'Tunisia',
		label: asLabel('countries-list.tunisia')
	},
	{
		id: 'Turkey',
		label: asLabel('countries-list.turkey'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Turkmenistan',
		label: asLabel('countries-list.turkmenistan')
	},
	{
		id: 'Turks and Caicos Islands',
		label: asLabel('countries-list.turks-and-caicos-islands')
	},
	{
		id: 'Tuvalu',
		label: asLabel('countries-list.tuvalu')
	},
	{
		id: 'Uganda',
		label: asLabel('countries-list.uganda'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'United Arab Emirates',
		label: asLabel('countries-list.united-arab-emirates')
	},
	{
		id: 'United Kingdom',
		label: asLabel('countries-list.united-kingdom'),
		dateFormat: 'mm-dd-yyyy'
	},
	{
		id: 'United States',
		label: asLabel('countries-list.united-states'),
		dateFormat: 'mm-dd-yyyy'
	},
	{
		id: 'Uruguay',
		label: asLabel('countries-list.uruguay')
	},
	{
		id: 'Uzbekistan',
		label: asLabel('countries-list.uzbekistan'),
		dateFormat: 'yyyy-mm-dd'
	},
	{
		id: 'Vanuatu',
		label: asLabel('countries-list.vanuatu')
	},
	{
		id: 'Venezuela, Bolivarian Republic of',
		label: asLabel('countries-list.venezuela,-bolivarian-republic-of')
	},
	{
		id: 'Viet Nam',
		label: asLabel('countries-list.vietnam'),
		dateFormat: 'dd/mm/yyyy'
	},
	{
		id: 'Virgin Islands, British',
		label: asLabel('countries-list.british-virgin-islands')
	},
	{
		id: 'Wallis and Futuna',
		label: asLabel('countries-list.wallis-and-futuna')
	},
	{
		id: 'Western Sahara',
		label: asLabel('countries-list.western-sahara')
	},
	{
		id: 'Yemen',
		label: asLabel('countries-list.yemen')
	},
	{
		id: 'Zambia',
		label: asLabel('countries-list.zambia')
	},
	{
		id: 'Zimbabwe',
		label: asLabel('countries-list.zimbabwe')
	}
];

export const TIMEZONE = [
	{
		id: 'Africa/Abidjan',
		label: asLabel('time-zone-list.africa-abidjan')
	},
	{
		id: 'Africa/Accra',
		label: asLabel('time-zone-list.africa-accra')
	},
	{
		id: 'Africa/Addis_Ababa',
		label: asLabel('time-zone-list.africa-addis-ababa')
	},
	{
		id: 'Africa/Algiers',
		label: asLabel('time-zone-list.africa-algiers')
	},
	{
		id: 'Africa/Asmera',
		label: asLabel('time-zone-list.africa-asmera')
	},
	{
		id: 'Africa/Bamako',
		label: asLabel('time-zone-list.africa-bamako')
	},
	{
		id: 'Africa/Bangui',
		label: asLabel('time-zone-list.africa-bangui')
	},
	{
		id: 'Africa/Banjul',
		label: asLabel('time-zone-list.africa-banjul')
	},
	{
		id: 'Africa/Bissau',
		label: asLabel('time-zone-list.africa-bissau')
	},
	{
		id: 'Africa/Blantyre',
		label: asLabel('time-zone-list.africa-blantyre')
	},
	{
		id: 'Africa/Brazzaville',
		label: asLabel('time-zone-list.africa-brazzaville')
	},
	{
		id: 'Africa/Bujumbura',
		label: asLabel('time-zone-list.africa-bujumbura')
	},
	{
		id: 'Africa/Cairo',
		label: asLabel('time-zone-list.africa-cairo')
	},
	{
		id: 'Africa/Casablanca',
		label: asLabel('time-zone-list.africa-casablanca')
	},
	{
		id: 'Africa/Ceuta',
		label: asLabel('time-zone-list.africa-ceuta')
	},
	{
		id: 'Africa/Conakry',
		label: asLabel('time-zone-list.africa-conakry')
	},
	{
		id: 'Africa/Dakar',
		label: asLabel('time-zone-list.africa-dakar')
	},
	{
		id: 'Africa/Dar_es_Salaam',
		label: asLabel('time-zone-list.africa-dar-es-salaam')
	},
	{
		id: 'Africa/Djibouti',
		label: asLabel('time-zone-list.africa-djibouti')
	},
	{
		id: 'Africa/Douala',
		label: asLabel('time-zone-list.africa-douala')
	},
	{
		id: 'Africa/El_Aaiun',
		label: asLabel('time-zone-list.africa-el-aaiun')
	},
	{
		id: 'Africa/Freetown',
		label: asLabel('time-zone-list.africa-freetown')
	},
	{
		id: 'Africa/Gaborone',
		label: asLabel('time-zone-list.africa-gaborone')
	},
	{
		id: 'Africa/Harare',
		label: asLabel('time-zone-list.africa-harare')
	},
	{
		id: 'Africa/Johannesburg',
		label: asLabel('time-zone-list.africa-johannesburg')
	},
	{
		id: 'Africa/Juba',
		label: asLabel('time-zone-list.africa-juba')
	},
	{
		id: 'Africa/Kampala',
		label: asLabel('time-zone-list.africa-kampala')
	},
	{
		id: 'Africa/Khartoum',
		label: asLabel('time-zone-list.africa-khartoum')
	},
	{
		id: 'Africa/Kigali',
		label: asLabel('time-zone-list.africa-kigali')
	},
	{
		id: 'Africa/Kinshasa',
		label: asLabel('time-zone-list.africa-kinshasa')
	},
	{
		id: 'Africa/Lagos',
		label: asLabel('time-zone-list.africa-lagos')
	},
	{
		id: 'Africa/Libreville',
		label: asLabel('time-zone-list.africa-libreville')
	},
	{
		id: 'Africa/Lome',
		label: asLabel('time-zone-list.africa-lome')
	},
	{
		id: 'Africa/Luanda',
		label: asLabel('time-zone-list.africa-luanda')
	},
	{
		id: 'Africa/Lubumbashi',
		label: asLabel('time-zone-list.africa-lubumbashi')
	},
	{
		id: 'Africa/Lusaka',
		label: asLabel('time-zone-list.africa-lusaka')
	},
	{
		id: 'Africa/Malabo',
		label: asLabel('time-zone-list.africa-malabo')
	},
	{
		id: 'Africa/Maputo',
		label: asLabel('time-zone-list.africa-maputo')
	},
	{
		id: 'Africa/Maseru',
		label: asLabel('time-zone-list.africa-maseru')
	},
	{
		id: 'Africa/Mbabane',
		label: asLabel('time-zone-list.africa-mbabane')
	},
	{
		id: 'Africa/Mogadishu',
		label: asLabel('time-zone-list.africa-mogadishu')
	},
	{
		id: 'Africa/Monrovia',
		label: asLabel('time-zone-list.africa-monrovia')
	},
	{
		id: 'Africa/Nairobi',
		label: asLabel('time-zone-list.africa-nairobi')
	},
	{
		id: 'Africa/Ndjamena',
		label: asLabel('time-zone-list.africa-ndjamena')
	},
	{
		id: 'Africa/Niamey',
		label: asLabel('time-zone-list.africa-niamey')
	},
	{
		id: 'Africa/Nouakchott',
		label: asLabel('time-zone-list.africa-nouakchott')
	},
	{
		id: 'Africa/Ouagadougou',
		label: asLabel('time-zone-list.africa-ouagadougou')
	},
	{
		id: 'Africa/Porto-Novo',
		label: asLabel('time-zone-list.africa-porto-novo')
	},
	{
		id: 'Africa/Sao_Tome',
		label: asLabel('time-zone-list.africa-sao-tome')
	},
	{
		id: 'Africa/Tripoli',
		label: asLabel('time-zone-list.africa-tripoli')
	},
	{
		id: 'Africa/Tunis',
		label: asLabel('time-zone-list.africa-tunis')
	},
	{
		id: 'Africa/Windhoek',
		label: asLabel('time-zone-list.africa-windhoek')
	},
	{
		id: 'America/Adak',
		label: asLabel('time-zone-list.america-adak')
	},
	{
		id: 'America/Anchorage',
		label: asLabel('time-zone-list.america-anchorage')
	},
	{
		id: 'America/Anguilla',
		label: asLabel('time-zone-list.america-anguilla')
	},
	{
		id: 'America/Antigua',
		label: asLabel('time-zone-list.america-antigua')
	},
	{
		id: 'America/Araguaina',
		label: asLabel('time-zone-list.america-araguaina')
	},
	{
		id: 'America/Argentina/Buenos_Aires',
		label: asLabel('time-zone-list.america-argentina-buenos-aires')
	},
	{
		id: 'America/Argentina/La_Rioja',
		label: asLabel('time-zone-list.america-argentina-la-rioja')
	},
	{
		id: 'America/Argentina/Rio_Gallegos',
		label: asLabel('time-zone-list.america-argentina-rio-gallegos')
	},
	{
		id: 'America/Argentina/Salta',
		label: asLabel('time-zone-list.america-argentina-salta')
	},
	{
		id: 'America/Argentina/San_Juan',
		label: asLabel('time-zone-list.america-argentina-san-juan')
	},
	{
		id: 'America/Argentina/San_Luis',
		label: asLabel('time-zone-list.america-argentina-san-luis')
	},
	{
		id: 'America/Argentina/Tucuman',
		label: asLabel('time-zone-list.america-argentina-tucuman')
	},
	{
		id: 'America/Argentina/Ushuaia',
		label: asLabel('time-zone-list.america-argentina-ushuaia')
	},
	{
		id: 'America/Aruba',
		label: asLabel('time-zone-list.america-aruba')
	},
	{
		id: 'America/Asuncion',
		label: asLabel('time-zone-list.america-asuncion')
	},
	{
		id: 'America/Bahia',
		label: asLabel('time-zone-list.america-bahia')
	},
	{
		id: 'America/Bahia_Banderas',
		label: asLabel('time-zone-list.america-bahia-banderas')
	},
	{
		id: 'America/Barbados',
		label: asLabel('time-zone-list.america-barbados')
	},
	{
		id: 'America/Belem',
		label: asLabel('time-zone-list.america-belem')
	},
	{
		id: 'America/Belize',
		label: asLabel('time-zone-list.america-belize')
	},
	{
		id: 'America/Blanc-Sablon',
		label: asLabel('time-zone-list.america-blanc-sablon')
	},
	{
		id: 'America/Boa_Vista',
		label: asLabel('time-zone-list.america-boa-vista')
	},
	{
		id: 'America/Bogota',
		label: asLabel('time-zone-list.america-bogota')
	},
	{
		id: 'America/Boise',
		label: asLabel('time-zone-list.america-boise')
	},
	{
		id: 'America/Cambridge_Bay',
		label: asLabel('time-zone-list.america-cambridge-bay')
	},
	{
		id: 'America/Campo_Grande',
		label: asLabel('time-zone-list.america-campo-grande')
	},
	{
		id: 'America/Cancun',
		label: asLabel('time-zone-list.america-cancun')
	},
	{
		id: 'America/Caracas',
		label: asLabel('time-zone-list.america-caracas')
	},
	{
		id: 'America/Catamarca',
		label: asLabel('time-zone-list.america-catamarca')
	},
	{
		id: 'America/Cayenne',
		label: asLabel('time-zone-list.america-cayenne')
	},
	{
		id: 'America/Cayman',
		label: asLabel('time-zone-list.america-cayman')
	},
	{
		id: 'America/Chicago',
		label: asLabel('time-zone-list.america-chicago')
	},
	{
		id: 'America/Chihuahua',
		label: asLabel('time-zone-list.america-chihuahua')
	},
	{
		id: 'America/Coral_Harbour',
		label: asLabel('time-zone-list.america-coral-harbour')
	},
	{
		id: 'America/Cordoba',
		label: asLabel('time-zone-list.america-cordoba')
	},
	{
		id: 'America/Costa_Rica',
		label: asLabel('time-zone-list.america-costa-rica')
	},
	{
		id: 'America/Creston',
		label: asLabel('time-zone-list.america-creston')
	},
	{
		id: 'America/Cuiaba',
		label: asLabel('time-zone-list.america-cuiaba')
	},
	{
		id: 'America/Curacao',
		label: asLabel('time-zone-list.america-curacao')
	},
	{
		id: 'America/Danmarkshavn',
		label: asLabel('time-zone-list.america-danmarkshavn')
	},
	{
		id: 'America/Dawson',
		label: asLabel('time-zone-list.america-dawso--n')
	},
	{
		id: 'America/Dawson_Creek',
		label: asLabel('time-zone-list.america-dawson-creek')
	},
	{
		id: 'America/Denver',
		label: asLabel('time-zone-list.america-denver')
	},
	{
		id: 'America/Detroit',
		label: asLabel('time-zone-list.america-detroit')
	},
	{
		id: 'America/Dominica',
		label: asLabel('time-zone-list.america-dominica')
	},
	{
		id: 'America/Edmonton',
		label: asLabel('time-zone-list.america-edmonton')
	},
	{
		id: 'America/Eirunepe',
		label: asLabel('time-zone-list.america-eirunepe')
	},
	{
		id: 'America/El_Salvador',
		label: asLabel('time-zone-list.america-el-salvador')
	},
	{
		id: 'America/Fort_Nelson',
		label: asLabel('time-zone-list.america-fort-nelson')
	},
	{
		id: 'America/Fortaleza',
		label: asLabel('time-zone-list.america-fortaleza')
	},
	{
		id: 'America/Glace_Bay',
		label: asLabel('time-zone-list.america-glace-bay')
	},
	{
		id: 'America/Godthab',
		label: asLabel('time-zone-list.america-godthab')
	},
	{
		id: 'America/Goose_Bay',
		label: asLabel('time-zone-list.america-goose-bay')
	},
	{
		id: 'America/Grand_Turk',
		label: asLabel('time-zone-list.america-grand-turk')
	},
	{
		id: 'America/Grenada',
		label: asLabel('time-zone-list.america-grenada')
	},
	{
		id: 'America/Guadeloupe',
		label: asLabel('time-zone-list.america-guadeloupe')
	},
	{
		id: 'America/Guatemala',
		label: asLabel('time-zone-list.america-guatemala')
	},
	{
		id: 'America/Guayaquil',
		label: asLabel('time-zone-list.america-guayaquil')
	},
	{
		id: 'America/Guyana',
		label: asLabel('time-zone-list.america-guyana')
	},
	{
		id: 'America/Halifax',
		label: asLabel('time-zone-list.america-halifax')
	},
	{
		id: 'America/Havana',
		label: asLabel('time-zone-list.america-havana')
	},
	{
		id: 'America/Hermosillo',
		label: asLabel('time-zone-list.america-hermosillo')
	},
	{
		id: 'America/Indiana/Indianapolis',
		label: asLabel('time-zone-list.america-indiana-indianapolis')
	},
	{
		id: 'America/Indiana/Knox',
		label: asLabel('time-zone-list.america-indiana-knox')
	},
	{
		id: 'America/Indiana/Marengo',
		label: asLabel('time-zone-list.america-indiana-marengo')
	},
	{
		id: 'America/Indiana/Petersburg',
		label: asLabel('time-zone-list.america-indiana-petersburg')
	},
	{
		id: 'America/Indiana/Tell_City',
		label: asLabel('time-zone-list.america-indiana-tell-city')
	},
	{
		id: 'America/Indiana/Vevay',
		label: asLabel('time-zone-list.america-indiana-vevay')
	},
	{
		id: 'America/Indiana/Vincennes',
		label: asLabel('time-zone-list.america-indiana-vincennes')
	},
	{
		id: 'America/Indiana/Winamac',
		label: asLabel('time-zone-list.america-indiana-winamac')
	},
	{
		id: 'America/Inuvik',
		label: asLabel('time-zone-list.america-inuvik')
	},
	{
		id: 'America/Iqaluit',
		label: asLabel('time-zone-list.america-iqaluit')
	},
	{
		id: 'America/Jamaica',
		label: asLabel('time-zone-list.america-jamaica')
	},
	{
		id: 'America/Jujuy',
		label: asLabel('time-zone-list.america-jujuy')
	},
	{
		id: 'America/Juneau',
		label: asLabel('time-zone-list.america-juneau')
	},
	{
		id: 'America/Kentucky/Monticello',
		label: asLabel('time-zone-list.america-kentucky-monticello')
	},
	{
		id: 'America/Kralendijk',
		label: asLabel('time-zone-list.america-kralendijk')
	},
	{
		id: 'America/La_Paz',
		label: asLabel('time-zone-list.america-la-paz')
	},
	{
		id: 'America/Lima',
		label: asLabel('time-zone-list.america-lima')
	},
	{
		id: 'America/Los_Angeles',
		label: asLabel('time-zone-list.america-los-angeles')
	},
	{
		id: 'America/Louisville',
		label: asLabel('time-zone-list.america-louisville')
	},
	{
		id: 'America/Lower_Princes',
		label: asLabel('time-zone-list.america-lower-princes')
	},
	{
		id: 'America/Maceio',
		label: asLabel('time-zone-list.america-maceio')
	},
	{
		id: 'America/Managua',
		label: asLabel('time-zone-list.america-managua')
	},
	{
		id: 'America/Manaus',
		label: asLabel('time-zone-list.america-manaus')
	},
	{
		id: 'America/Marigot',
		label: asLabel('time-zone-list.america-marigot')
	},
	{
		id: 'America/Martinique',
		label: asLabel('time-zone-list.america-martinique')
	},
	{
		id: 'America/Matamoros',
		label: asLabel('time-zone-list.america-matamoros')
	},
	{
		id: 'America/Mazatlan',
		label: asLabel('time-zone-list.america-mazatlan')
	},
	{
		id: 'America/Mendoza',
		label: asLabel('time-zone-list.america-mendoza')
	},
	{
		id: 'America/Menominee',
		label: asLabel('time-zone-list.america-menominee')
	},
	{
		id: 'America/Merida',
		label: asLabel('time-zone-list.america-merida')
	},
	{
		id: 'America/Metlakatla',
		label: asLabel('time-zone-list.america-metlakatla')
	},
	{
		id: 'America/Mexico_City',
		label: asLabel('time-zone-list.america-mexico-city')
	},
	{
		id: 'America/Miquelon',
		label: asLabel('time-zone-list.america-miquelon')
	},
	{
		id: 'America/Moncton',
		label: asLabel('time-zone-list.america-moncton')
	},
	{
		id: 'America/Monterrey',
		label: asLabel('time-zone-list.america-monterrey')
	},
	{
		id: 'America/Montevideo',
		label: asLabel('time-zone-list.america-montevideo')
	},
	{
		id: 'America/Montreal',
		label: asLabel('time-zone-list.america-montreal')
	},
	{
		id: 'America/Montserrat',
		label: asLabel('time-zone-list.america-montserrat')
	},
	{
		id: 'America/Nassau',
		label: asLabel('time-zone-list.america-nassau')
	},
	{
		id: 'America/New_York',
		label: asLabel('time-zone-list.america-new-york')
	},
	{
		id: 'America/Nipigon',
		label: asLabel('time-zone-list.america-nipigon')
	},
	{
		id: 'America/Nome',
		label: asLabel('time-zone-list.america-nome')
	},
	{
		id: 'America/Noronha',
		label: asLabel('time-zone-list.america-noronha')
	},
	{
		id: 'America/North_Dakota/Beulah',
		label: asLabel('time-zone-list.america-north-dakota-beulah')
	},
	{
		id: 'America/North_Dakota/Center',
		label: asLabel('time-zone-list.america-north-dakota-center')
	},
	{
		id: 'America/North_Dakota/New_Salem',
		label: asLabel('time-zone-list.america-north-dakota-new-salem')
	},
	{
		id: 'America/Ojinaga',
		label: asLabel('time-zone-list.america-ojinaga')
	},
	{
		id: 'America/Panama',
		label: asLabel('time-zone-list.america-panama')
	},
	{
		id: 'America/Pangnirtung',
		label: asLabel('time-zone-list.america-pangnirtung')
	},
	{
		id: 'America/Paramaribo',
		label: asLabel('time-zone-list.america-paramaribo')
	},
	{
		id: 'America/Phoenix',
		label: asLabel('time-zone-list.america-phoenix')
	},
	{
		id: 'America/Port-au-Prince',
		label: asLabel('time-zone-list.america-port-au-prince')
	},
	{
		id: 'America/Port_of_Spain',
		label: asLabel('time-zone-list.america-port-of-spain')
	},
	{
		id: 'America/Porto_Velho',
		label: asLabel('time-zone-list.america-porto-velho')
	},
	{
		id: 'America/Puerto_Rico',
		label: asLabel('time-zone-list.america-puerto-rico')
	},
	{
		id: 'America/Punta_Arenas',
		label: asLabel('time-zone-list.america-punta-arenas')
	},
	{
		id: 'America/Rainy_River',
		label: asLabel('time-zone-list.america-rainy-river')
	},
	{
		id: 'America/Rankin_Inlet',
		label: asLabel('time-zone-list.america-rankin-inlet')
	},
	{
		id: 'America/Recife',
		label: asLabel('time-zone-list.america-recife')
	},
	{
		id: 'America/Regina',
		label: asLabel('time-zone-list.america-regina')
	},
	{
		id: 'America/Resolute',
		label: asLabel('time-zone-list.america-resolute')
	},
	{
		id: 'America/Rio_Branco',
		label: asLabel('time-zone-list.america-rio-branco')
	},
	{
		id: 'America/Santa_Isabel',
		label: asLabel('time-zone-list.america-santa-isabel')
	},
	{
		id: 'America/Santarem',
		label: asLabel('time-zone-list.america-santarem')
	},
	{
		id: 'America/Santiago',
		label: asLabel('time-zone-list.america-santiago')
	},
	{
		id: 'America/Santo_Domingo',
		label: asLabel('time-zone-list.america-santo-domingo')
	},
	{
		id: 'America/Sao_Paulo',
		label: asLabel('time-zone-list.america-sao-paulo')
	},
	{
		id: 'America/Scoresbysund',
		label: asLabel('time-zone-list.america-scoresbysund')
	},
	{
		id: 'America/Sitka',
		label: asLabel('time-zone-list.america-sitka')
	},
	{
		id: 'America/St_Barthelemy',
		label: asLabel('time-zone-list.america-st-barthelemy')
	},
	{
		id: 'America/St_Johns',
		label: asLabel('time-zone-list.america-st-johns')
	},
	{
		id: 'America/St_Kitts',
		label: asLabel('time-zone-list.america-st-kitts')
	},
	{
		id: 'America/St_Lucia',
		label: asLabel('time-zone-list.america-st-lucia')
	},
	{
		id: 'America/St_Thomas',
		label: asLabel('time-zone-list.america-st-thomas')
	},
	{
		id: 'America/St_Vincent',
		label: asLabel('time-zone-list.america-st-vincent')
	},
	{
		id: 'America/Swift_Current',
		label: asLabel('time-zone-list.america-swift-current')
	},
	{
		id: 'America/Tegucigalpa',
		label: asLabel('time-zone-list.america-tegucigalpa')
	},
	{
		id: 'America/Thule',
		label: asLabel('time-zone-list.america-thule')
	},
	{
		id: 'America/Thunder_Bay',
		label: asLabel('time-zone-list.america-thunder-bay')
	},
	{
		id: 'America/Tijuana',
		label: asLabel('time-zone-list.america-tijuana')
	},
	{
		id: 'America/Toronto',
		label: asLabel('time-zone-list.america-toronto')
	},
	{
		id: 'America/Tortola',
		label: asLabel('time-zone-list.america-tortola')
	},
	{
		id: 'America/Vancouver',
		label: asLabel('time-zone-list.america-vancouver')
	},
	{
		id: 'America/Whitehorse',
		label: asLabel('time-zone-list.america-whitehorse')
	},
	{
		id: 'America/Winnipeg',
		label: asLabel('time-zone-list.america-winnipeg')
	},
	{
		id: 'America/Yakutat',
		label: asLabel('time-zone-list.america-yakutat')
	},
	{
		id: 'America/Yellowknife',
		label: asLabel('time-zone-list.america-yellowknife')
	},
	{
		id: 'Antarctica/Casey',
		label: asLabel('time-zone-list.antarctica-casey')
	},
	{
		id: 'Antarctica/Davis',
		label: asLabel('time-zone-list.antarctica-davis')
	},
	{
		id: 'Antarctica/DumontDUrville',
		label: asLabel('time-zone-list.antarctica-dumontdurville')
	},
	{
		id: 'Antarctica/Macquarie',
		label: asLabel('time-zone-list.antarctica-macquarie')
	},
	{
		id: 'Antarctica/Mawson',
		label: asLabel('time-zone-list.antarctica-mawson')
	},
	{
		id: 'Antarctica/McMurdo',
		label: asLabel('time-zone-list.antarctica-mcmurdo')
	},
	{
		id: 'Antarctica/Palmer',
		label: asLabel('time-zone-list.antarctica-palmer')
	},
	{
		id: 'Antarctica/Rothera',
		label: asLabel('time-zone-list.antarctica-rothera')
	},
	{
		id: 'Antarctica/Syowa',
		label: asLabel('time-zone-list.antarctica-syowa')
	},
	{
		id: 'Antarctica/Troll',
		label: asLabel('time-zone-list.antarctica-troll')
	},
	{
		id: 'Antarctica/Vostok',
		label: asLabel('time-zone-list.antarctica-vostok')
	},
	{
		id: 'Arctic/Longyearbyen',
		label: asLabel('time-zone-list.arctic-longyearbyen')
	},
	{
		id: 'Asia/Aden',
		label: asLabel('time-zone-list.asia-aden')
	},
	{
		id: 'Asia/Almaty',
		label: asLabel('time-zone-list.asia-almaty')
	},
	{
		id: 'Asia/Amman',
		label: asLabel('time-zone-list.asia-amman')
	},
	{
		id: 'Asia/Anadyr',
		label: asLabel('time-zone-list.asia-anadyr')
	},
	{
		id: 'Asia/Aqtau',
		label: asLabel('time-zone-list.asia-aqtau')
	},
	{
		id: 'Asia/Aqtobe',
		label: asLabel('time-zone-list.asia-aqtobe')
	},
	{
		id: 'Asia/Ashgabat',
		label: asLabel('time-zone-list.asia-ashgabat')
	},
	{
		id: 'Asia/Atyrau',
		label: asLabel('time-zone-list.asia-atyrau')
	},
	{
		id: 'Asia/Baghdad',
		label: asLabel('time-zone-list.asia-baghdad')
	},
	{
		id: 'Asia/Bahrain',
		label: asLabel('time-zone-list.asia-bahrain')
	},
	{
		id: 'Asia/Baku',
		label: asLabel('time-zone-list.asia-baku')
	},
	{
		id: 'Asia/Bangkok',
		label: asLabel('time-zone-list.asia-bangkok')
	},
	{
		id: 'Asia/Barnaul',
		label: asLabel('time-zone-list.asia-barnaul')
	},
	{
		id: 'Asia/Beirut',
		label: asLabel('time-zone-list.asia-beirut')
	},
	{
		id: 'Asia/Bishkek',
		label: asLabel('time-zone-list.asia-bishkek')
	},
	{
		id: 'Asia/Brunei',
		label: asLabel('time-zone-list.asia-brunei')
	},
	{
		id: 'Asia/Chita',
		label: asLabel('time-zone-list.asia-chita')
	},
	{
		id: 'Asia/Choibalsan',
		label: asLabel('time-zone-list.asia-choibalsan')
	},
	{
		id: 'Asia/Colombo',
		label: asLabel('time-zone-list.asia-colombo')
	},
	{
		id: 'Asia/Damascus',
		label: asLabel('time-zone-list.asia-damascus')
	},
	{
		id: 'Asia/Dhaka',
		label: asLabel('time-zone-list.asia-dhaka')
	},
	{
		id: 'Asia/Dili',
		label: asLabel('time-zone-list.asia-dili')
	},
	{
		id: 'Asia/Dubai',
		label: asLabel('time-zone-list.asia-dubai')
	},
	{
		id: 'Asia/Dushanbe',
		label: asLabel('time-zone-list.asia-dushanbe')
	},
	{
		id: 'Asia/Famagusta',
		label: asLabel('time-zone-list.asia-famagusta')
	},
	{
		id: 'Asia/Gaza',
		label: asLabel('time-zone-list.asia-gaza')
	},
	{
		id: 'Asia/Hebron',
		label: asLabel('time-zone-list.asia-hebron')
	},
	{
		id: 'Asia/Ho_Chi_Minh',
		label: asLabel('time-zone-list.asia-ho-chi-minh')
	},
	{
		id: 'Asia/Hong_Kong',
		label: asLabel('time-zone-list.asia-hong-kong')
	},
	{
		id: 'Asia/Hovd',
		label: asLabel('time-zone-list.asia-hovd')
	},
	{
		id: 'Asia/Irkutsk',
		label: asLabel('time-zone-list.asia-irkutsk')
	},
	{
		id: 'Asia/Jakarta',
		label: asLabel('time-zone-list.asia-jakarta')
	},
	{
		id: 'Asia/Jayapura',
		label: asLabel('time-zone-list.asia-jayapura')
	},
	{
		id: 'Asia/Jerusalem',
		label: asLabel('time-zone-list.asia-jerusalem')
	},
	{
		id: 'Asia/Kabul',
		label: asLabel('time-zone-list.asia-kabul')
	},
	{
		id: 'Asia/Kamchatka',
		label: asLabel('time-zone-list.asia-kamchatka')
	},
	{
		id: 'Asia/Karachi',
		label: asLabel('time-zone-list.asia-karachi')
	},
	{
		id: 'Asia/Kathmandu',
		label: asLabel('time-zone-list.asia-kathmandu')
	},
	{
		id: 'Asia/Khandyga',
		label: asLabel('time-zone-list.asia-khandyga')
	},
	{
		id: 'Asia/Kolkata',
		label: asLabel('time-zone-list.asia-kolkata')
	},
	{
		id: 'Asia/Krasnoyarsk',
		label: asLabel('time-zone-list.asia-krasnoyarsk')
	},
	{
		id: 'Asia/Kuala_Lumpur',
		label: asLabel('time-zone-list.asia-kuala-lumpur')
	},
	{
		id: 'Asia/Kuching',
		label: asLabel('time-zone-list.asia-kuching')
	},
	{
		id: 'Asia/Kuwait',
		label: asLabel('time-zone-list.asia-kuwait')
	},
	{
		id: 'Asia/Macau',
		label: asLabel('time-zone-list.asia-macau')
	},
	{
		id: 'Asia/Magadan',
		label: asLabel('time-zone-list.asia-magadan')
	},
	{
		id: 'Asia/Makassar',
		label: asLabel('time-zone-list.asia-makassar')
	},
	{
		id: 'Asia/Manila',
		label: asLabel('time-zone-list.asia-manila')
	},
	{
		id: 'Asia/Muscat',
		label: asLabel('time-zone-list.asia-muscat')
	},
	{
		id: 'Asia/Nicosia',
		label: asLabel('time-zone-list.asia-nicosia')
	},
	{
		id: 'Asia/Novokuznetsk',
		label: asLabel('time-zone-list.asia-novokuznetsk')
	},
	{
		id: 'Asia/Novosibirsk',
		label: asLabel('time-zone-list.asia-novosibirsk')
	},
	{
		id: 'Asia/Omsk',
		label: asLabel('time-zone-list.asia-omsk')
	},
	{
		id: 'Asia/Oral',
		label: asLabel('time-zone-list.asia-oral')
	},
	{
		id: 'Asia/Phnom_Penh',
		label: asLabel('time-zone-list.asia-phnom-penh')
	},
	{
		id: 'Asia/Pontianak',
		label: asLabel('time-zone-list.asia-pontianak')
	},
	{
		id: 'Asia/Pyongyang',
		label: asLabel('time-zone-list.asia-pyongyang')
	},
	{
		id: 'Asia/Qatar',
		label: asLabel('time-zone-list.asia-qatar')
	},
	{
		id: 'Asia/Qostanay',
		label: asLabel('time-zone-list.asia-qostanay')
	},
	{
		id: 'Asia/Qyzylorda',
		label: asLabel('time-zone-list.asia-qyzylorda')
	},
	{
		id: 'Asia/Rangoon',
		label: asLabel('time-zone-list.asia-rangoon')
	},
	{
		id: 'Asia/Riyadh',
		label: asLabel('time-zone-list.asia-riyadh')
	},
	{
		id: 'Asia/Sakhalin',
		label: asLabel('time-zone-list.asia-sakhalin')
	},
	{
		id: 'Asia/Samarkand',
		label: asLabel('time-zone-list.asia-samarkand')
	},
	{
		id: 'Asia/Seoul',
		label: asLabel('time-zone-list.asia-seoul')
	},
	{
		id: 'Asia/Shanghai',
		label: asLabel('time-zone-list.asia-shanghai')
	},
	{
		id: 'Asia/Singapore',
		label: asLabel('time-zone-list.asia-singapore')
	},
	{
		id: 'Asia/Srednekolymsk',
		label: asLabel('time-zone-list.asia-srednekolymsk')
	},
	{
		id: 'Asia/Taipei',
		label: asLabel('time-zone-list.asia-taipei')
	},
	{
		id: 'Asia/Tashkent',
		label: asLabel('time-zone-list.asia-tashkent')
	},
	{
		id: 'Asia/Tbilisi',
		label: asLabel('time-zone-list.asia-tbilisi')
	},
	{
		id: 'Asia/Tehran',
		label: asLabel('time-zone-list.asia-tehran')
	},
	{
		id: 'Asia/Thimphu',
		label: asLabel('time-zone-list.asia-thimphu')
	},
	{
		id: 'Asia/Tokyo',
		label: asLabel('time-zone-list.asia-tokyo')
	},
	{
		id: 'Asia/Tomsk',
		label: asLabel('time-zone-list.asia-tomsk')
	},
	{
		id: 'Asia/Ulaanbaatar',
		label: asLabel('time-zone-list.asia-ulaanbaatar')
	},
	{
		id: 'Asia/Urumqi',
		label: asLabel('time-zone-list.asia-urumqi')
	},
	{
		id: 'Asia/Ust-Nera',
		label: asLabel('time-zone-list.asia-ust-nera')
	},
	{
		id: 'Asia/Vientiane',
		label: asLabel('time-zone-list.asia-vientiane')
	},
	{
		id: 'Asia/Vladivostok',
		label: asLabel('time-zone-list.asia-vladivostok')
	},
	{
		id: 'Asia/Yakutsk',
		label: asLabel('time-zone-list.asia-yakutsk')
	},
	{
		id: 'Asia/Yekaterinburg',
		label: asLabel('time-zone-list.asia-yekaterinburg')
	},
	{
		id: 'Asia/Yerevan',
		label: asLabel('time-zone-list.asia-yerevan')
	},
	{
		id: 'Atlantic/Azores',
		label: asLabel('time-zone-list.atlantic-azores')
	},
	{
		id: 'Atlantic/Bermuda',
		label: asLabel('time-zone-list.atlantic-bermuda')
	},
	{
		id: 'Atlantic/Canary',
		label: asLabel('time-zone-list.atlantic-canary')
	},
	{
		id: 'Atlantic/Cape_Verde',
		label: asLabel('time-zone-list.atlantic-cape-verde')
	},
	{
		id: 'Atlantic/Faeroe',
		label: asLabel('time-zone-list.atlantic-faeroe')
	},
	{
		id: 'Atlantic/Madeira',
		label: asLabel('time-zone-list.atlantic-madeira')
	},
	{
		id: 'Atlantic/Reykjavik',
		label: asLabel('time-zone-list.atlantic-reykjavik')
	},
	{
		id: 'Atlantic/South_Georgia',
		label: asLabel('time-zone-list.atlantic-south-georgia')
	},
	{
		id: 'Atlantic/St_Helena',
		label: asLabel('time-zone-list.atlantic-st-helena')
	},
	{
		id: 'Atlantic/Stanley',
		label: asLabel('time-zone-list.atlantic-stanley')
	},
	{
		id: 'Australia/Adelaide',
		label: asLabel('time-zone-list.australia-adelaide')
	},
	{
		id: 'Australia/Brisbane',
		label: asLabel('time-zone-list.australia-brisbane')
	},
	{
		id: 'Australia/Broken_Hill',
		label: asLabel('time-zone-list.australia-broken-hill')
	},
	{
		id: 'Australia/Currie',
		label: asLabel('time-zone-list.australia-currie')
	},
	{
		id: 'Australia/Darwin',
		label: asLabel('time-zone-list.australia-darwin')
	},
	{
		id: 'Australia/Eucla',
		label: asLabel('time-zone-list.australia-eucla')
	},
	{
		id: 'Australia/Hobart',
		label: asLabel('time-zone-list.australia-hobart')
	},
	{
		id: 'Australia/Lindeman',
		label: asLabel('time-zone-list.australia-lindeman')
	},
	{
		id: 'Australia/Lord_Howe',
		label: asLabel('time-zone-list.australia-lord-howe')
	},
	{
		id: 'Australia/Melbourne',
		label: asLabel('time-zone-list.australia-melbourne')
	},
	{
		id: 'Australia/Perth',
		label: asLabel('time-zone-list.australia-perth')
	},
	{
		id: 'Australia/Sydney',
		label: asLabel('time-zone-list.australia-sydney')
	},
	{
		id: 'Europe/Amsterdam',
		label: asLabel('time-zone-list.europe-amsterdam')
	},
	{
		id: 'Europe/Andorra',
		label: asLabel('time-zone-list.europe-andorra')
	},
	{
		id: 'Europe/Astrakhan',
		label: asLabel('time-zone-list.europe-astrakhan')
	},
	{
		id: 'Europe/Athens',
		label: asLabel('time-zone-list.europe-athens')
	},
	{
		id: 'Europe/Belgrade',
		label: asLabel('time-zone-list.europe-belgrade')
	},
	{
		id: 'Europe/Berlin',
		label: asLabel('time-zone-list.europe-berlin')
	},
	{
		id: 'Europe/Bratislava',
		label: asLabel('time-zone-list.europe-bratislava')
	},
	{
		id: 'Europe/Brussels',
		label: asLabel('time-zone-list.europe-brussels')
	},
	{
		id: 'Europe/Bucharest',
		label: asLabel('time-zone-list.europe-bucharest')
	},
	{
		id: 'Europe/Budapest',
		label: asLabel('time-zone-list.europe-budapest')
	},
	{
		id: 'Europe/Busingen',
		label: asLabel('time-zone-list.europe-busingen')
	},
	{
		id: 'Europe/Chisinau',
		label: asLabel('time-zone-list.europe-chisinau')
	},
	{
		id: 'Europe/Copenhagen',
		label: asLabel('time-zone-list.europe-copenhagen')
	},
	{
		id: 'Europe/Dublin',
		label: asLabel('time-zone-list.europe-dublin')
	},
	{
		id: 'Europe/Gibraltar',
		label: asLabel('time-zone-list.europe-gibraltar')
	},
	{
		id: 'Europe/Guernsey',
		label: asLabel('time-zone-list.europe-guernsey')
	},
	{
		id: 'Europe/Helsinki',
		label: asLabel('time-zone-list.europe-helsinki')
	},
	{
		id: 'Europe/Isle_of_Man',
		label: asLabel('time-zone-list.europe-isle-of-man')
	},
	{
		id: 'Europe/Istanbul',
		label: asLabel('time-zone-list.europe-istanbul')
	},
	{
		id: 'Europe/Jersey',
		label: asLabel('time-zone-list.europe-jersey')
	},
	{
		id: 'Europe/Kaliningrad',
		label: asLabel('time-zone-list.europe-kaliningrad')
	},
	{
		id: 'Europe/Kirov',
		label: asLabel('time-zone-list.europe-kirov')
	},
	{
		id: 'Europe/Kyiv',
		label: asLabel('time-zone-list.europe-kyiv')
	},
	{
		id: 'Europe/Lisbon',
		label: asLabel('time-zone-list.europe-lisbon')
	},
	{
		id: 'Europe/Ljubljana',
		label: asLabel('time-zone-list.europe-ljubljana')
	},
	{
		id: 'Europe/London',
		label: asLabel('time-zone-list.europe-london')
	},
	{
		id: 'Europe/Luxembourg',
		label: asLabel('time-zone-list.europe-luxembourg')
	},
	{
		id: 'Europe/Madrid',
		label: asLabel('time-zone-list.europe-madrid')
	},
	{
		id: 'Europe/Malta',
		label: asLabel('time-zone-list.europe-malta')
	},
	{
		id: 'Europe/Mariehamn',
		label: asLabel('time-zone-list.europe-mariehamn')
	},
	{
		id: 'Europe/Minsk',
		label: asLabel('time-zone-list.europe-minsk')
	},
	{
		id: 'Europe/Monaco',
		label: asLabel('time-zone-list.europe-monaco')
	},
	{
		id: 'Europe/Moscow',
		label: asLabel('time-zone-list.europe-moscow')
	},
	{
		id: 'Europe/Oslo',
		label: asLabel('time-zone-list.europe-oslo')
	},
	{
		id: 'Europe/Paris',
		label: asLabel('time-zone-list.europe-paris')
	},
	{
		id: 'Europe/Podgorica',
		label: asLabel('time-zone-list.europe-podgorica')
	},
	{
		id: 'Europe/Prague',
		label: asLabel('time-zone-list.europe-prague')
	},
	{
		id: 'Europe/Riga',
		label: asLabel('time-zone-list.europe-riga')
	},
	{
		id: 'Europe/Rome',
		label: asLabel('time-zone-list.europe-rome')
	},
	{
		id: 'Europe/Samara',
		label: asLabel('time-zone-list.europe-samara')
	},
	{
		id: 'Europe/San_Marino',
		label: asLabel('time-zone-list.europe-san-marino')
	},
	{
		id: 'Europe/Sarajevo',
		label: asLabel('time-zone-list.europe-sarajevo')
	},
	{
		id: 'Europe/Saratov',
		label: asLabel('time-zone-list.europe-saratov')
	},
	{
		id: 'Europe/Simferopol',
		label: asLabel('time-zone-list.europe-simferopol')
	},
	{
		id: 'Europe/Skopje',
		label: asLabel('time-zone-list.europe-skopje')
	},
	{
		id: 'Europe/Sofia',
		label: asLabel('time-zone-list.europe-sofia')
	},
	{
		id: 'Europe/Stockholm',
		label: asLabel('time-zone-list.europe-stockholm')
	},
	{
		id: 'Europe/Tallinn',
		label: asLabel('time-zone-list.europe-tallinn')
	},
	{
		id: 'Europe/Tirane',
		label: asLabel('time-zone-list.europe-tirane')
	},
	{
		id: 'Europe/Ulyanovsk',
		label: asLabel('time-zone-list.europe-ulyanovsk')
	},
	{
		id: 'Europe/Uzhgorod',
		label: asLabel('time-zone-list.europe-uzhgorod')
	},
	{
		id: 'Europe/Vaduz',
		label: asLabel('time-zone-list.europe-vaduz')
	},
	{
		id: 'Europe/Vatican',
		label: asLabel('time-zone-list.europe-vatican')
	},
	{
		id: 'Europe/Vienna',
		label: asLabel('time-zone-list.europe-vienna')
	},
	{
		id: 'Europe/Vilnius',
		label: asLabel('time-zone-list.europe-vilnius')
	},
	{
		id: 'Europe/Volgograd',
		label: asLabel('time-zone-list.europe-volgograd')
	},
	{
		id: 'Europe/Warsaw',
		label: asLabel('time-zone-list.europe-warsaw')
	},
	{
		id: 'Europe/Zagreb',
		label: asLabel('time-zone-list.europe-zagreb')
	},
	{
		id: 'Europe/Zaporozhye',
		label: asLabel('time-zone-list.europe-zaporozhye')
	},
	{
		id: 'Europe/Zurich',
		label: asLabel('time-zone-list.europe-zurich')
	},
	{
		id: 'GMT',
		label: asLabel('time-zone-list.gmt')
	},
	{
		id: 'Indian/Antananarivo',
		label: asLabel('time-zone-list.indian-antananarivo')
	},
	{
		id: 'Indian/Chagos',
		label: asLabel('time-zone-list.indian-chagos')
	},
	{
		id: 'Indian/Christmas',
		label: asLabel('time-zone-list.indian-christmas')
	},
	{
		id: 'Indian/Cocos',
		label: asLabel('time-zone-list.indian-cocos')
	},
	{
		id: 'Indian/Comoro',
		label: asLabel('time-zone-list.indian-comoro')
	},
	{
		id: 'Indian/Kerguelen',
		label: asLabel('time-zone-list.indian-kerguelen')
	},
	{
		id: 'Indian/Mahe',
		label: asLabel('time-zone-list.indian-mahe')
	},
	{
		id: 'Indian/Maldives',
		label: asLabel('time-zone-list.indian-maldives')
	},
	{
		id: 'Indian/Mauritius',
		label: asLabel('time-zone-list.indian-mauritius')
	},
	{
		id: 'Indian/Mayotte',
		label: asLabel('time-zone-list.indian-mayotte')
	},
	{
		id: 'Indian/Reunion',
		label: asLabel('time-zone-list.indian-reunion')
	},
	{
		id: 'Pacific/Apia',
		label: asLabel('time-zone-list.pacific-apia')
	},
	{
		id: 'Pacific/Auckland',
		label: asLabel('time-zone-list.pacific-auckland')
	},
	{
		id: 'Pacific/Bougainville',
		label: asLabel('time-zone-list.pacific-bougainville')
	},
	{
		id: 'Pacific/Chatham',
		label: asLabel('time-zone-list.pacific-chatham')
	},
	{
		id: 'Pacific/Easter',
		label: asLabel('time-zone-list.pacific-easter')
	},
	{
		id: 'Pacific/Efate',
		label: asLabel('time-zone-list.pacific-efate')
	},
	{
		id: 'Pacific/Enderbury',
		label: asLabel('time-zone-list.pacific-enderbury')
	},
	{
		id: 'Pacific/Fakaofo',
		label: asLabel('time-zone-list.pacific-fakaofo')
	},
	{
		id: 'Pacific/Fiji',
		label: asLabel('time-zone-list.pacific-fiji')
	},
	{
		id: 'Pacific/Funafuti',
		label: asLabel('time-zone-list.pacific-funafuti')
	},
	{
		id: 'Pacific/Galapagos',
		label: asLabel('time-zone-list.pacific-galapagos')
	},
	{
		id: 'Pacific/Gambier',
		label: asLabel('time-zone-list.pacific-gambier')
	},
	{
		id: 'Pacific/Guadalcanal',
		label: asLabel('time-zone-list.pacific-guadalcanal')
	},
	{
		id: 'Pacific/Guam',
		label: asLabel('time-zone-list.pacific-guam')
	},
	{
		id: 'Pacific/Honolulu',
		label: asLabel('time-zone-list.pacific-honolulu')
	},
	{
		id: 'Pacific/Johnston',
		label: asLabel('time-zone-list.pacific-johnston')
	},
	{
		id: 'Pacific/Kiritimati',
		label: asLabel('time-zone-list.pacific-kiritimati')
	},
	{
		id: 'Pacific/Kosrae',
		label: asLabel('time-zone-list.pacific-kosrae')
	},
	{
		id: 'Pacific/Kwajalein',
		label: asLabel('time-zone-list.pacific-kwajalein')
	},
	{
		id: 'Pacific/Majuro',
		label: asLabel('time-zone-list.pacific-majuro')
	},
	{
		id: 'Pacific/Marquesas',
		label: asLabel('time-zone-list.pacific-marquesas')
	},
	{
		id: 'Pacific/Midway',
		label: asLabel('time-zone-list.pacific-midway')
	},
	{
		id: 'Pacific/Nauru',
		label: asLabel('time-zone-list.pacific-nauru')
	},
	{
		id: 'Pacific/Niue',
		label: asLabel('time-zone-list.pacific-niue')
	},
	{
		id: 'Pacific/Norfolk',
		label: asLabel('time-zone-list.pacific-norfolk')
	},
	{
		id: 'Pacific/Noumea',
		label: asLabel('time-zone-list.pacific-noumea')
	},
	{
		id: 'Pacific/Pago_Pago',
		label: asLabel('time-zone-list.pacific-pago-pago')
	},
	{
		id: 'Pacific/Palau',
		label: asLabel('time-zone-list.pacific-palau')
	},
	{
		id: 'Pacific/Pitcairn',
		label: asLabel('time-zone-list.pacific-pitcairn')
	},
	{
		id: 'Pacific/Ponape',
		label: asLabel('time-zone-list.pacific-ponape')
	},
	{
		id: 'Pacific/Port_Moresby',
		label: asLabel('time-zone-list.pacific-port-moresby')
	},
	{
		id: 'Pacific/Rarotonga',
		label: asLabel('time-zone-list.pacific-rarotonga')
	},
	{
		id: 'Pacific/Saipan',
		label: asLabel('time-zone-list.pacific-saipan')
	},
	{
		id: 'Pacific/Tahiti',
		label: asLabel('time-zone-list.pacific-tahiti')
	},
	{
		id: 'Pacific/Tarawa',
		label: asLabel('time-zone-list.pacific-tarawa')
	},
	{
		id: 'Pacific/Tongatapu',
		label: asLabel('time-zone-list.pacific-tongatapu')
	},
	{
		id: 'Pacific/Truk',
		label: asLabel('time-zone-list.pacific-truk')
	},
	{
		id: 'Pacific/Wake',
		label: asLabel('time-zone-list.pacific-wake')
	},
	{
		id: 'Pacific/Wallis',
		label: asLabel('time-zone-list.pacific-wallis')
	}
];

export const COUNTRIES_AND_TIMEZONES = [
	{
		id: 'Afghanistan',
		isoCode: 'AF',
		label: asLabel('countries-list.afghanistan'),
		timezones: [
			{
				id: 'Asia/Kabul',
				timezone: asLabel('time-zone-list.asia-kabul')
			}
		]
	},
	{
		id: 'Albania',
		isoCode: 'AL',
		label: asLabel('countries-list.albania'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Tirane',
				timezone: asLabel('time-zone-list.europe-tirane')
			}
		]
	},
	{
		id: 'Algeria',
		isoCode: 'DZ',
		label: asLabel('countries-list.algeria'),
		timezones: [
			{
				id: 'Africa/Algiers',
				timezone: asLabel('time-zone-list.africa-algiers')
			}
		]
	},
	{
		id: 'Argentina',
		isoCode: 'AR',
		label: asLabel('countries-list.argentina'),
		timezones: [
			{
				id: 'America/Argentina/Buenos_Aires',
				timezone: asLabel('time-zone-list.america-argentina-buenos-aires')
			},
			{
				id: 'America/Argentina/Catamarca',
				timezone: asLabel('time-zone-list.america-argentina-catamarca')
			},
			{
				id: 'America/Argentina/Cordoba',
				timezone: asLabel('time-zone-list.america-argentina-cordoba')
			},
			{
				id: 'America/Argentina/Jujuy',
				timezone: asLabel('time-zone-list.america-argentina-jujuy')
			},
			{
				id: 'America/Argentina/La_Rioja',
				timezone: asLabel('time-zone-list.america-argentina-la-rioja')
			},
			{
				id: 'America/Argentina/Mendoza',
				timezone: asLabel('time-zone-list.america-argentina-mendoza')
			},
			{
				id: 'America/Argentina/Rio_Gallegos',
				timezone: asLabel('time-zone-list.america-argentina-rio-gallegos')
			},
			{
				id: 'America/Argentina/Salta',
				timezone: asLabel('time-zone-list.america-argentina-salta')
			},
			{
				id: 'America/Argentina/San_Juan',
				timezone: asLabel('time-zone-list.america-argentina-san-juan')
			},
			{
				id: 'America/Argentina/San_Luis',
				timezone: asLabel('time-zone-list.america-argentina-san-luis')
			},
			{
				id: 'America/Argentina/Tucuman',
				timezone: asLabel('time-zone-list.america-argentina-tucuman')
			},
			{
				id: 'America/Argentina/Ushuaia',
				timezone: asLabel('time-zone-list.america-argentina-ushuaia')
			}
		]
	},
	{
		id: 'Armenia',
		isoCode: 'AM',
		label: asLabel('countries-list.armenia'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Asia/Yerevan',
				timezone: asLabel('time-zone-list.asia-yerevan')
			}
		]
	},
	{
		id: 'Australia',
		isoCode: 'AU',
		label: asLabel('countries-list.australia'),
		dateFormat: 'dd-mm-yyyy',
		timezones: [
			{
				id: 'Antarctica/Macquarie',
				timezone: asLabel('time-zone-list.antarctica-macquarie')
			},
			{
				id: 'Asia/Tokyo',
				timezone: asLabel('time-zone-list.asia-tokyo')
			},
			{
				id: 'Australia/Adelaide',
				timezone: asLabel('time-zone-list.australia-adelaide')
			},
			{
				id: 'Australia/Brisbane',
				timezone: asLabel('time-zone-list.australia-brisbane')
			},
			{
				id: 'Australia/Broken_Hill',
				timezone: asLabel('time-zone-list.australia-broken-hill')
			},
			{
				id: 'Australia/Darwin',
				timezone: asLabel('time-zone-list.australia-darwin')
			},
			{
				id: 'Australia/Eucla',
				timezone: asLabel('time-zone-list.australia-eucla')
			},
			{
				id: 'Australia/Hobart',
				timezone: asLabel('time-zone-list.australia-hobart')
			},
			{
				id: 'Australia/Lindeman',
				timezone: asLabel('time-zone-list.australia-lindeman')
			},
			{
				id: 'Australia/Lord_Howe',
				timezone: asLabel('time-zone-list.australia-lord-howe')
			},
			{
				id: 'Australia/Melbourne',
				timezone: asLabel('time-zone-list.australia-melbourne')
			},
			{
				id: 'Australia/Perth',
				timezone: asLabel('time-zone-list.australia-perth')
			},
			{
				id: 'Australia/Sydney',
				timezone: asLabel('time-zone-list.australia-sydney')
			}
		]
	},
	{
		id: 'Austria',
		isoCode: 'AT',
		label: asLabel('countries-list.austria'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Vienna',
				timezone: asLabel('time-zone-list.europe-vienna')
			}
		]
	},
	{
		id: 'Azerbaijan',
		isoCode: 'AZ',
		label: asLabel('countries-list.azerbaijan'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Asia/Baku',
				timezone: asLabel('time-zone-list.asia-baku')
			}
		]
	},
	{
		id: 'Bangladesh',
		isoCode: 'BD',
		label: asLabel('countries-list.bangladesh'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Dhaka',
				timezone: asLabel('time-zone-list.asia-dhaka')
			}
		]
	},
	{
		id: 'Barbados',
		isoCode: 'BB',
		label: asLabel('countries-list.barbados'),
		timezones: [
			{
				id: 'America/Barbados',
				timezone: asLabel('time-zone-list.america-barbados')
			}
		]
	},
	{
		id: 'Belgium',
		isoCode: 'BE',
		label: asLabel('countries-list.belgium'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Brussels',
				timezone: asLabel('time-zone-list.europe-brussels')
			}
		]
	},
	{
		id: 'Bermuda',
		isoCode: 'BM',
		label: asLabel('countries-list.bermuda'),
		timezones: [
			{
				id: 'Atlantic/Bermuda',
				timezone: asLabel('time-zone-list.atlantic-bermuda')
			}
		]
	},
	{
		id: 'Bolivia, Plurinational State of',
		isoCode: 'BO',
		label: asLabel('countries-list.bolivia'),
		timezones: [
			{
				id: 'America/La_Paz',
				timezone: asLabel('time-zone-list.america-la-paz')
			}
		]
	},
	{
		id: 'Bosnia and Herzegovina',
		isoCode: 'BA',
		label: asLabel('countries-list.bosnia-and-herzegovina'),
		timezones: [
			{
				id: 'Europe/Belgrade',
				timezone: asLabel('time-zone-list.europe-belgrade')
			}
		]
	},
	{
		id: 'Brazil',
		isoCode: 'BR',
		label: asLabel('countries-list.brazil'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'America/Araguaina',
				timezone: asLabel('time-zone-list.america-araguaina')
			},
			{
				id: 'America/Bahia',
				timezone: asLabel('time-zone-list.america-bahia')
			},
			{
				id: 'America/Belem',
				timezone: asLabel('time-zone-list.america-belem')
			},
			{
				id: 'America/Boa_Vista',
				timezone: asLabel('time-zone-list.america-boa-vista')
			},
			{
				id: 'America/Campo_Grande',
				timezone: asLabel('time-zone-list.america-campo-grande')
			},
			{
				id: 'America/Cuiaba',
				timezone: asLabel('time-zone-list.america-cuiaba')
			},
			{
				id: 'America/Eirunepe',
				timezone: asLabel('time-zone-list.america-eirunepe')
			},
			{
				id: 'America/Fortaleza',
				timezone: asLabel('time-zone-list.america-fortaleza')
			},
			{
				id: 'America/Maceio',
				timezone: asLabel('time-zone-list.america-maceio')
			},
			{
				id: 'America/Manaus',
				timezone: asLabel('time-zone-list.america-manaus')
			},
			{
				id: 'America/Noronha',
				timezone: asLabel('time-zone-list.america-noronha')
			},
			{
				id: 'America/Porto_Velho',
				timezone: asLabel('time-zone-list.america-porto-velho')
			},
			{
				id: 'America/Recife',
				timezone: asLabel('time-zone-list.america-recife')
			},
			{
				id: 'America/Rio_Branco',
				timezone: asLabel('time-zone-list.america-rio-branco')
			},
			{
				id: 'America/Santarem',
				timezone: asLabel('time-zone-list.america-santarem')
			},
			{
				id: 'America/Sao_Paulo',
				timezone: asLabel('time-zone-list.america-sao-paulo')
			}
		]
	},
	{
		id: 'Virgin Islands, British',
		isoCode: 'VG',
		label: asLabel('countries-list.british-virgin-islands'),
		timezones: [
			{
				id: 'America/Puerto_Rico',
				timezone: asLabel('time-zone-list.america-puerto-rico')
			}
		]
	},
	{
		id: 'Bulgaria',
		isoCode: 'BG',
		label: asLabel('countries-list.bulgaria'),
		dateFormat: 'dd.mm.yyyy',
		timezones: [
			{
				id: 'Europe/Sofia',
				timezone: asLabel('time-zone-list.europe-sofia')
			}
		]
	},
	{
		id: 'Cambodia',
		isoCode: 'KH',
		label: asLabel('countries-list.cambodia'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Bangkok',
				timezone: asLabel('time-zone-list.asia-bangkok')
			}
		]
	},
	{
		id: 'Cameroon',
		isoCode: 'CM',
		label: asLabel('countries-list.cameroon'),
		timezones: [
			{
				id: 'Africa/Lagos',
				timezone: asLabel('time-zone-list.africa-lagos')
			}
		]
	},
	{
		id: 'Canada',
		isoCode: 'CA',
		label: asLabel('countries-list.canada'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'America/Cambridge_Bay',
				timezone: asLabel('time-zone-list.america-cambridge-bay')
			},
			{
				id: 'America/Dawson',
				timezone: asLabel('time-zone-list.america-dawso--n')
			},
			{
				id: 'America/Dawson_Creek',
				timezone: asLabel('time-zone-list.america-dawson-creek')
			},
			{
				id: 'America/Edmonton',
				timezone: asLabel('time-zone-list.america-edmonton')
			},
			{
				id: 'America/Fort_Nelson',
				timezone: asLabel('time-zone-list.america-fort-nelson')
			},
			{
				id: 'America/Glace_Bay',
				timezone: asLabel('time-zone-list.america-glace-bay')
			},
			{
				id: 'America/Goose_Bay',
				timezone: asLabel('time-zone-list.america-goose-bay')
			},
			{
				id: 'America/Halifax',
				timezone: asLabel('time-zone-list.america-halifax')
			},
			{
				id: 'America/Inuvik',
				timezone: asLabel('time-zone-list.america-inuvik')
			},
			{
				id: 'America/Iqaluit',
				timezone: asLabel('time-zone-list.america-iqaluit')
			},
			{
				id: 'America/Moncton',
				timezone: asLabel('time-zone-list.america-moncton')
			},
			{
				id: 'America/Panama',
				timezone: asLabel('time-zone-list.america-panama')
			},
			{
				id: 'America/Phoenix',
				timezone: asLabel('time-zone-list.america-phoenix')
			},
			{
				id: 'America/Puerto_Rico',
				timezone: asLabel('time-zone-list.america-puerto-rico')
			},
			{
				id: 'America/Rankin_Inlet',
				timezone: asLabel('time-zone-list.america-rankin-inlet')
			},
			{
				id: 'America/Regina',
				timezone: asLabel('time-zone-list.america-regina')
			},
			{
				id: 'America/Resolute',
				timezone: asLabel('time-zone-list.america-resolute')
			},
			{
				id: 'America/St_Johns',
				timezone: asLabel('time-zone-list.america-st-johns')
			},
			{
				id: 'America/Swift_Current',
				timezone: asLabel('time-zone-list.america-swift-current')
			},
			{
				id: 'America/Toronto',
				timezone: asLabel('time-zone-list.america-toronto')
			},
			{
				id: 'America/Vancouver',
				timezone: asLabel('time-zone-list.america-vancouver')
			},
			{
				id: 'America/Whitehorse',
				timezone: asLabel('time-zone-list.america-whitehorse')
			},
			{
				id: 'America/Winnipeg',
				timezone: asLabel('time-zone-list.america-winnipeg')
			}
		]
	},
	{
		id: 'Cayman Islands',
		isoCode: 'KY',
		label: asLabel('countries-list.cayman-islands'),
		timezones: [
			{
				id: 'America/Panama',
				timezone: asLabel('time-zone-list.america-panama')
			}
		]
	},
	{
		id: 'Chile',
		isoCode: 'CL',
		label: asLabel('countries-list.chile'),
		timezones: [
			// {
			// 	id: 'America/Coyhaique',
			// 	timezone: asLabel('time-zone-list.america-coyhaique')
			// },
			{
				id: 'America/Punta_Arenas',
				timezone: asLabel('time-zone-list.america-punta-arenas')
			},
			{
				id: 'America/Santiago',
				timezone: asLabel('time-zone-list.america-santiago')
			},
			{
				id: 'Pacific/Easter',
				timezone: asLabel('time-zone-list.pacific-easter')
			}
		]
	},
	{
		id: 'China',
		isoCode: 'CN',
		label: asLabel('countries-list.china'),
		dateFormat: 'mm/dd/yyyy',
		timezones: [
			{
				id: 'Asia/Shanghai',
				timezone: asLabel('time-zone-list.asia-shanghai')
			},
			{
				id: 'Asia/Urumqi',
				timezone: asLabel('time-zone-list.asia-urumqi')
			}
		]
	},
	{
		id: 'Chinese Taipei',
		isoCode: 'TW',
		label: asLabel('countries-list.chinese-taipei'),
		dateFormat: 'mm-dd-yyyy',
		timezones: [
			{
				id: 'Asia/Taipei',
				timezone: asLabel('time-zone-list.asia-taipei')
			}
		]
	},
	{
		id: 'Colombia',
		isoCode: 'CO',
		label: asLabel('countries-list.colombia'),
		timezones: [
			{
				id: 'America/Bogota',
				timezone: asLabel('time-zone-list.america-bogota')
			}
		]
	},
	{
		id: 'Costa Rica',
		isoCode: 'CR',
		label: asLabel('countries-list.costa-rica'),
		timezones: [
			{
				id: 'America/Costa_Rica',
				timezone: asLabel('time-zone-list.america-costa-rica')
			}
		]
	},
	{
		// eslint-disable-next-line prettier/prettier
		id: 'Cote d\'Ivoire',
		isoCode: 'CI',
		label: asLabel('countries-list.cote-divoire'),
		timezones: [
			{
				id: 'Africa/Abidjan',
				timezone: asLabel('time-zone-list.africa-abidjan')
			}
		]
	},
	{
		id: 'Croatia',
		isoCode: 'HR',
		label: asLabel('countries-list.croatia'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Belgrade',
				timezone: asLabel('time-zone-list.europe-belgrade')
			}
		]
	},
	{
		id: 'Curaçao',
		isoCode: 'CW',
		label: asLabel('countries-list.curacao'),
		timezones: [
			{
				id: 'America/Puerto_Rico',
				timezone: asLabel('time-zone-list.america-puerto-rico')
			}
		]
	},
	{
		id: 'Cyprus',
		isoCode: 'CY',
		label: asLabel('countries-list.cyprus'),
		timezones: [
			{
				id: 'Asia/Famagusta',
				timezone: asLabel('time-zone-list.asia-famagusta')
			},
			{
				id: 'Asia/Nicosia',
				timezone: asLabel('time-zone-list.asia-nicosia')
			}
		]
	},
	{
		id: 'Czech Republic',
		isoCode: 'CZ',
		label: asLabel('countries-list.czech-republic'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Prague',
				timezone: asLabel('time-zone-list.europe-prague')
			}
		]
	},
	{
		id: 'Denmark',
		isoCode: 'DK',
		label: asLabel('countries-list.denmark'),
		dateFormat: 'dd-mm-yyyy',
		timezones: [
			{
				id: 'Europe/Berlin',
				timezone: asLabel('time-zone-list.europe-berlin')
			}
		]
	},
	{
		id: 'Congo, the Democratic Republic of the',
		isoCode: 'CD',
		label: asLabel('countries-list.democratic-republic-of-the-congo'),
		timezones: [
			{
				id: 'Africa/Lagos',
				timezone: asLabel('time-zone-list.africa-lagos')
			},
			{
				id: 'Africa/Maputo',
				timezone: asLabel('time-zone-list.africa-maputo')
			}
		]
	},
	{
		id: 'Dominican Republic',
		isoCode: 'DO',
		label: asLabel('countries-list.dominican-republic'),
		timezones: [
			{
				id: 'America/Santo_Domingo',
				timezone: asLabel('time-zone-list.america-santo-domingo')
			}
		]
	},
	{
		id: 'Ecuador',
		isoCode: 'EC',
		label: asLabel('countries-list.ecuador'),
		timezones: [
			{
				id: 'America/Guayaquil',
				timezone: asLabel('time-zone-list.america-guayaquil')
			},
			{
				id: 'Pacific/Galapagos',
				timezone: asLabel('time-zone-list.pacific-galapagos')
			}
		]
	},
	{
		id: 'Egypt',
		isoCode: 'EG',
		label: asLabel('countries-list.egypt'),
		timezones: [
			{
				id: 'Africa/Cairo',
				timezone: asLabel('time-zone-list.africa-cairo')
			}
		]
	},
	{
		id: 'El Salvador',
		isoCode: 'SV',
		label: asLabel('countries-list.el-salvador'),
		timezones: [
			{
				id: 'America/El_Salvador',
				timezone: asLabel('time-zone-list.america-el-salvador')
			}
		]
	},
	{
		id: 'Estonia',
		isoCode: 'EE',
		label: asLabel('countries-list.estonia'),
		dateFormat: 'dd-mm-yyyy',
		timezones: [
			{
				id: 'Europe/Tallinn',
				timezone: asLabel('time-zone-list.europe-tallinn')
			}
		]
	},
	{
		id: 'Ethiopia',
		isoCode: 'ET',
		label: asLabel('countries-list.ethiopia'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Africa/Nairobi',
				timezone: asLabel('time-zone-list.africa-nairobi')
			}
		]
	},
	{
		id: 'Falkland Islands (Malvinas)',
		isoCode: 'FK',
		label: asLabel('countries-list.falkland-islands'),
		timezones: [
			{
				id: 'Atlantic/Stanley',
				timezone: asLabel('time-zone-list.atlantic-stanley')
			}
		]
	},
	{
		id: 'Fiji',
		isoCode: 'FJ',
		label: asLabel('countries-list.fiji'),
		timezones: [
			{
				id: 'Pacific/Fiji',
				timezone: asLabel('time-zone-list.pacific-fiji')
			}
		]
	},
	{
		id: 'Finland',
		isoCode: 'FI',
		label: asLabel('countries-list.finland'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Helsinki',
				timezone: asLabel('time-zone-list.europe-helsinki')
			}
		]
	},
	{
		id: 'France',
		isoCode: 'FR',
		label: asLabel('countries-list.france'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Paris',
				timezone: asLabel('time-zone-list.europe-paris')
			}
		]
	},
	{
		id: 'Georgia',
		isoCode: 'GE',
		label: asLabel('countries-list.georgia'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Asia/Tbilisi',
				timezone: asLabel('time-zone-list.asia-tbilisi')
			}
		]
	},
	{
		id: 'Germany',
		isoCode: 'DE',
		label: asLabel('countries-list.germany'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Berlin',
				timezone: asLabel('time-zone-list.europe-berlin')
			},
			{
				id: 'Europe/Zurich',
				timezone: asLabel('time-zone-list.europe-zurich')
			}
		]
	},
	{
		id: 'Ghana',
		isoCode: 'GH',
		label: asLabel('countries-list.ghana'),
		timezones: [
			{
				id: 'Africa/Abidjan',
				timezone: asLabel('time-zone-list.africa-abidjan')
			}
		]
	},
	{
		id: 'Greece',
		isoCode: 'GR',
		label: asLabel('countries-list.greece'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Athens',
				timezone: asLabel('time-zone-list.europe-athens')
			}
		]
	},
	{
		id: 'Guatemala',
		isoCode: 'GT',
		label: asLabel('countries-list.guatemala'),
		timezones: [
			{
				id: 'America/Guatemala',
				timezone: asLabel('time-zone-list.america-guatemala')
			}
		]
	},
	{
		id: 'Guinea',
		isoCode: 'GN',
		label: asLabel('countries-list.guinea'),
		timezones: [
			{
				id: 'Africa/Abidjan',
				timezone: asLabel('time-zone-list.africa-abidjan')
			}
		]
	},
	{
		id: 'Honduras',
		isoCode: 'HN',
		label: asLabel('countries-list.honduras'),
		timezones: [
			{
				id: 'America/Tegucigalpa',
				timezone: asLabel('time-zone-list.america-tegucigalpa')
			}
		]
	},
	{
		id: 'Hong Kong S.A.R.',
		isoCode: 'HK',
		label: asLabel('countries-list.hong-kong-s.a.r.'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Asia/Hong_Kong',
				timezone: asLabel('time-zone-list.asia-hong-kong')
			}
		]
	},
	{
		id: 'Hungary',
		isoCode: 'HU',
		label: asLabel('countries-list.hungary'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Budapest',
				timezone: asLabel('time-zone-list.europe-budapest')
			}
		]
	},
	{
		id: 'Iceland',
		isoCode: 'IS',
		label: asLabel('countries-list.iceland'),
		dateFormat: 'dd-mm-yyyy',
		timezones: [
			{
				id: 'Africa/Abidjan',
				timezone: asLabel('time-zone-list.africa-abidjan')
			}
		]
	},
	{
		id: 'India',
		isoCode: 'IN',
		label: asLabel('countries-list.india'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Kolkata',
				timezone: asLabel('time-zone-list.asia-kolkata')
			}
		]
	},
	{
		id: 'Indonesia',
		isoCode: 'ID',
		label: asLabel('countries-list.indonesia'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Jakarta',
				timezone: asLabel('time-zone-list.asia-jakarta')
			},
			{
				id: 'Asia/Jayapura',
				timezone: asLabel('time-zone-list.asia-jayapura')
			},
			{
				id: 'Asia/Makassar',
				timezone: asLabel('time-zone-list.asia-makassar')
			},
			{
				id: 'Asia/Pontianak',
				timezone: asLabel('time-zone-list.asia-pontianak')
			}
		]
	},
	{
		id: 'Ireland',
		isoCode: 'IE',
		label: asLabel('countries-list.ireland'),
		dateFormat: 'mm-dd-yyyy',
		timezones: [
			{
				id: 'Europe/Dublin',
				timezone: asLabel('time-zone-list.europe-dublin')
			}
		]
	},
	{
		id: 'Israel',
		isoCode: 'IL',
		label: asLabel('countries-list.israel'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Jerusalem',
				timezone: asLabel('time-zone-list.asia-jerusalem')
			}
		]
	},
	{
		id: 'Italy',
		isoCode: 'IT',
		label: asLabel('countries-list.italy'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Rome',
				timezone: asLabel('time-zone-list.europe-rome')
			}
		]
	},
	{
		id: 'Jamaica',
		isoCode: 'JM',
		label: asLabel('countries-list.jamaica'),
		timezones: [
			{
				id: 'America/Jamaica',
				timezone: asLabel('time-zone-list.america-jamaica')
			}
		]
	},
	{
		id: 'Japan',
		isoCode: 'JP',
		label: asLabel('countries-list.japan'),
		dateFormat: 'mm/dd/yyyy',
		timezones: [
			{
				id: 'Asia/Tokyo',
				timezone: asLabel('time-zone-list.asia-tokyo')
			}
		]
	},
	{
		id: 'Jordan',
		isoCode: 'JO',
		label: asLabel('countries-list.jordan'),
		timezones: [
			{
				id: 'Asia/Amman',
				timezone: asLabel('time-zone-list.asia-amman')
			}
		]
	},
	{
		id: 'Kazakhstan',
		isoCode: 'KZ',
		label: asLabel('countries-list.kazakhstan'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Almaty',
				timezone: asLabel('time-zone-list.asia-almaty')
			},
			{
				id: 'Asia/Aqtau',
				timezone: asLabel('time-zone-list.asia-aqtau')
			},
			{
				id: 'Asia/Aqtobe',
				timezone: asLabel('time-zone-list.asia-aqtobe')
			},
			{
				id: 'Asia/Atyrau',
				timezone: asLabel('time-zone-list.asia-atyrau')
			},
			{
				id: 'Asia/Oral',
				timezone: asLabel('time-zone-list.asia-oral')
			},
			{
				id: 'Asia/Qostanay',
				timezone: asLabel('time-zone-list.asia-qostanay')
			},
			{
				id: 'Asia/Qyzylorda',
				timezone: asLabel('time-zone-list.asia-qyzylorda')
			}
		]
	},
	{
		id: 'Kenya',
		isoCode: 'KE',
		label: asLabel('countries-list.kenya'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Africa/Nairobi',
				timezone: asLabel('time-zone-list.africa-nairobi')
			}
		]
	},
	{
		id: 'Kuwait',
		isoCode: 'KW',
		label: asLabel('countries-list.kuwait'),
		timezones: [
			{
				id: 'Asia/Riyadh',
				timezone: asLabel('time-zone-list.asia-riyadh')
			}
		]
	},
	{
		id: 'Latvia',
		isoCode: 'LV',
		label: asLabel('countries-list.latvia'),
		dateFormat: 'yyyy.dd.mm',
		timezones: [
			{
				id: 'Europe/Riga',
				timezone: asLabel('time-zone-list.europe-riga')
			}
		]
	},
	{
		id: 'Lao Peoples Democratic Republic',
		isoCode: 'LA',
		label: asLabel('countries-list.laos'),
		timezones: [
			{
				id: 'Asia/Bangkok',
				timezone: asLabel('time-zone-list.asia-bangkok')
			}
		]
	},
	{
		id: 'Lebanon',
		isoCode: 'LB',
		label: asLabel('countries-list.lebanon'),
		timezones: [
			{
				id: 'Asia/Beirut',
				timezone: asLabel('time-zone-list.asia-beirut')
			}
		]
	},
	{
		id: 'Lithuania',
		isoCode: 'LT',
		label: asLabel('countries-list.lithuania'),
		dateFormat: 'yyyy.mm.dd',
		timezones: [
			{
				id: 'Europe/Vilnius',
				timezone: asLabel('time-zone-list.europe-vilnius')
			}
		]
	},
	{
		id: 'Luxembourg',
		isoCode: 'LU',
		label: asLabel('countries-list.luxembourg'),
		timezones: [
			{
				id: 'Europe/Brussels',
				timezone: asLabel('time-zone-list.europe-brussels')
			}
		]
	},
	{
		id: 'Macao',
		isoCode: 'MO',
		label: asLabel('countries-list.macau'),
		timezones: [
			{
				id: 'Asia/Macau',
				timezone: asLabel('time-zone-list.asia-macau')
			}
		]
	},
	{
		id: 'Macedonia, the former Yugoslav Republic of',
		isoCode: 'MK',
		label: asLabel('countries-list.macedonia'),
		timezones: [
			{
				id: 'Europe/Belgrade',
				timezone: asLabel('time-zone-list.europe-belgrade')
			}
		]
	},
	{
		id: 'Malawi',
		isoCode: 'MW',
		label: asLabel('countries-list.malawi'),
		timezones: [
			{
				id: 'Africa/Maputo',
				timezone: asLabel('time-zone-list.africa-maputo')
			}
		]
	},
	{
		id: 'Malaysia',
		isoCode: 'MY',
		label: asLabel('countries-list.malaysia'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Kuching',
				timezone: asLabel('time-zone-list.asia-kuching')
			},
			{
				id: 'Asia/Singapore',
				timezone: asLabel('time-zone-list.asia-singapore')
			}
		]
	},
	{
		id: 'Maldives',
		isoCode: 'MV',
		label: asLabel('countries-list.maldives'),
		timezones: [
			{
				id: 'Indian/Maldives',
				timezone: asLabel('time-zone-list.indian-maldives')
			}
		]
	},
	{
		id: 'Malta',
		isoCode: 'MT',
		label: asLabel('countries-list.malta'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Malta',
				timezone: asLabel('time-zone-list.europe-malta')
			}
		]
	},
	{
		id: 'Mauritius',
		isoCode: 'MU',
		label: asLabel('countries-list.mauritius'),
		timezones: [
			{
				id: 'Indian/Mauritius',
				timezone: asLabel('time-zone-list.indian-mauritius')
			}
		]
	},
	{
		id: 'Mexico',
		isoCode: 'MX',
		label: asLabel('countries-list.mexico'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'America/Bahia_Banderas',
				timezone: asLabel('time-zone-list.america-bahia-banderas')
			},
			{
				id: 'America/Cancun',
				timezone: asLabel('time-zone-list.america-cancun')
			},
			{
				id: 'America/Chihuahua',
				timezone: asLabel('time-zone-list.america-chihuahua')
			},
			{
				id: 'America/Ciudad_Juarez',
				timezone: asLabel('time-zone-list.america-ciudad-juarez')
			},
			{
				id: 'America/Hermosillo',
				timezone: asLabel('time-zone-list.america-hermosillo')
			},
			{
				id: 'America/Matamoros',
				timezone: asLabel('time-zone-list.america-matamoros')
			},
			{
				id: 'America/Mazatlan',
				timezone: asLabel('time-zone-list.america-mazatlan')
			},
			{
				id: 'America/Merida',
				timezone: asLabel('time-zone-list.america-merida')
			},
			{
				id: 'America/Mexico_City',
				timezone: asLabel('time-zone-list.america-mexico-city')
			},
			{
				id: 'America/Monterrey',
				timezone: asLabel('time-zone-list.america-monterrey')
			},
			{
				id: 'America/Ojinaga',
				timezone: asLabel('time-zone-list.america-ojinaga')
			},
			{
				id: 'America/Tijuana',
				timezone: asLabel('time-zone-list.america-tijuana')
			}
		]
	},
	{
		id: 'Moldova, Republic of',
		isoCode: 'MD',
		label: asLabel('countries-list.moldova'),
		timezones: [
			{
				id: 'Europe/Chisinau',
				timezone: asLabel('time-zone-list.europe-chisinau')
			}
		]
	},
	{
		id: 'Mongolia',
		isoCode: 'MN',
		label: asLabel('countries-list.mongolia'),
		timezones: [
			{
				id: 'Asia/Hovd',
				timezone: asLabel('time-zone-list.asia-hovd')
			},
			{
				id: 'Asia/Ulaanbaatar',
				timezone: asLabel('time-zone-list.asia-ulaanbaatar')
			}
		]
	},
	{
		id: 'Montenegro',
		isoCode: 'ME',
		label: asLabel('countries-list.montenegro'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Belgrade',
				timezone: asLabel('time-zone-list.europe-belgrade')
			}
		]
	},
	{
		id: 'Morocco',
		isoCode: 'MA',
		label: asLabel('countries-list.morocco'),
		timezones: [
			{
				id: 'Africa/Casablanca',
				timezone: asLabel('time-zone-list.africa-casablanca')
			}
		]
	},
	{
		id: 'Mozambique',
		isoCode: 'MZ',
		label: asLabel('countries-list.mozambique'),
		timezones: [
			{
				id: 'Africa/Maputo',
				timezone: asLabel('time-zone-list.africa-maputo')
			}
		]
	},
	{
		id: 'Myanmar',
		isoCode: 'MM',
		label: asLabel('countries-list.myanmar'),
		timezones: [
			{
				id: 'Asia/Yangon',
				timezone: asLabel('time-zone-list.asia-yangon')
			}
		]
	},
	{
		id: 'Nepal',
		isoCode: 'NP',
		label: asLabel('countries-list.nepal'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Asia/Kathmandu',
				timezone: asLabel('time-zone-list.asia-kathmandu')
			}
		]
	},
	{
		id: 'Netherlands',
		isoCode: 'NL',
		label: asLabel('countries-list.netherlands'),
		dateFormat: 'dd-mm-yyyy',
		timezones: [
			{
				id: 'Europe/Brussels',
				timezone: asLabel('time-zone-list.europe-brussels')
			}
		]
	},
	{
		id: 'New Zealand',
		isoCode: 'NZ',
		label: asLabel('countries-list.new-zealand'),
		dateFormat: 'dd-mm-yyyy',
		timezones: [
			{
				id: 'Pacific/Auckland',
				timezone: asLabel('time-zone-list.pacific-auckland')
			},
			{
				id: 'Pacific/Chatham',
				timezone: asLabel('time-zone-list.pacific-chatham')
			}
		]
	},
	{
		id: 'Nicaragua',
		isoCode: 'NI',
		label: asLabel('countries-list.nicaragua'),
		timezones: [
			{
				id: 'America/Managua',
				timezone: asLabel('time-zone-list.america-managua')
			}
		]
	},
	{
		id: 'Nigeria',
		isoCode: 'NG',
		label: asLabel('countries-list.nigeria'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Africa/Lagos',
				timezone: asLabel('time-zone-list.africa-lagos')
			}
		]
	},
	{
		id: 'Norway',
		isoCode: 'NO',
		label: asLabel('countries-list.norway'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Berlin',
				timezone: asLabel('time-zone-list.europe-berlin')
			}
		]
	},
	{
		id: 'Oman',
		isoCode: 'OM',
		label: asLabel('countries-list.oman'),
		timezones: [
			{
				id: 'Asia/Dubai',
				timezone: asLabel('time-zone-list.asia-dubai')
			}
		]
	},
	{
		id: 'Pakistan',
		isoCode: 'PK',
		label: asLabel('countries-list.pakistan'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Karachi',
				timezone: asLabel('time-zone-list.asia-karachi')
			}
		]
	},
	{
		id: 'Palestinian Territory, Occupied',
		isoCode: 'PS',
		label: asLabel('countries-list.palestinian-territory'),
		timezones: [
			{
				id: 'Asia/Gaza',
				timezone: asLabel('time-zone-list.asia-gaza')
			},
			{
				id: 'Asia/Hebron',
				timezone: asLabel('time-zone-list.asia-hebron')
			}
		]
	},
	{
		id: 'Panama',
		isoCode: 'PA',
		label: asLabel('countries-list.panama'),
		timezones: [
			{
				id: 'America/Panama',
				timezone: asLabel('time-zone-list.america-panama')
			}
		]
	},
	{
		id: 'Paraguay',
		isoCode: 'PY',
		label: asLabel('countries-list.paraguay'),
		timezones: [
			{
				id: 'America/Asuncion',
				timezone: asLabel('time-zone-list.america-asuncion')
			}
		]
	},
	{
		id: 'Peru',
		isoCode: 'PE',
		label: asLabel('countries-list.peru'),
		timezones: [
			{
				id: 'America/Lima',
				timezone: asLabel('time-zone-list.america-lima')
			}
		]
	},
	{
		id: 'Philippines',
		isoCode: 'PH',
		label: asLabel('countries-list.philippines'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Asia/Manila',
				timezone: asLabel('time-zone-list.asia-manila')
			}
		]
	},
	{
		id: 'Poland',
		isoCode: 'PL',
		label: asLabel('countries-list.poland'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Warsaw',
				timezone: asLabel('time-zone-list.europe-warsaw')
			}
		]
	},
	{
		id: 'Portugal',
		isoCode: 'PT',
		label: asLabel('countries-list.portugal'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Atlantic/Azores',
				timezone: asLabel('time-zone-list.atlantic-azores')
			},
			{
				id: 'Atlantic/Madeira',
				timezone: asLabel('time-zone-list.atlantic-madeira')
			},
			{
				id: 'Europe/Lisbon',
				timezone: asLabel('time-zone-list.europe-lisbon')
			}
		]
	},
	{
		id: 'Puerto Rico',
		isoCode: 'PR',
		label: asLabel('countries-list.puerto-rico'),
		timezones: [
			{
				id: 'America/Puerto_Rico',
				timezone: asLabel('time-zone-list.america-puerto-rico')
			}
		]
	},
	{
		id: 'Qatar',
		isoCode: 'QA',
		label: asLabel('countries-list.qatar'),
		timezones: [
			{
				id: 'Asia/Qatar',
				timezone: asLabel('time-zone-list.asia-qatar')
			}
		]
	},
	{
		id: 'Reunion',
		isoCode: 'RE',
		label: asLabel('countries-list.reunion'),
		timezones: [
			{
				id: 'Asia/Dubai',
				timezone: asLabel('time-zone-list.asia-dubai')
			}
		]
	},
	{
		id: 'Romania',
		isoCode: 'RO',
		label: asLabel('countries-list.romania'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Bucharest',
				timezone: asLabel('time-zone-list.europe-bucharest')
			}
		]
	},
	{
		id: 'Samoa',
		isoCode: 'WS',
		label: asLabel('countries-list.samoa'),
		timezones: [
			{
				id: 'Pacific/Apia',
				timezone: asLabel('time-zone-list.pacific-apia')
			}
		]
	},
	{
		id: 'Saudi Arabia',
		isoCode: 'SA',
		label: asLabel('countries-list.saudi-arabia'),
		timezones: [
			{
				id: 'Asia/Riyadh',
				timezone: asLabel('time-zone-list.asia-riyadh')
			}
		]
	},
	{
		id: 'Serbia',
		isoCode: 'RS ',
		label: asLabel('countries-list.serbia'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Belgrade',
				timezone: asLabel('time-zone-list.europe-belgrade')
			}
		]
	},
	{
		id: 'Seychelles',
		isoCode: 'SC',
		label: asLabel('countries-list.seychelles'),
		timezones: [
			{
				id: 'Asia/Dubai',
				timezone: asLabel('time-zone-list.asia-dubai')
			}
		]
	},
	{
		id: 'Singapore',
		isoCode: 'SG',
		label: asLabel('countries-list.singapore'),
		timezones: [
			{
				id: 'Asia/Singapore',
				timezone: asLabel('time-zone-list.asia-singapore')
			}
		]
	},
	{
		id: 'Slovakia',
		isoCode: 'SK',
		label: asLabel('countries-list.slovakia'),
		dateFormat: 'yyyy-mm-dd',
		timezones: [
			{
				id: 'Europe/Prague',
				timezone: asLabel('time-zone-list.europe-prague')
			}
		]
	},
	{
		id: 'Slovenia',
		isoCode: 'SI',
		label: asLabel('countries-list.slovenia'),
		dateFormat: 'dd-mm-yyyy',
		timezones: [
			{
				id: 'Europe/Belgrade',
				timezone: asLabel('time-zone-list.europe-belgrade')
			}
		]
	},
	{
		id: 'South Africa',
		isoCode: 'ZA',
		label: asLabel('countries-list.south-africa'),
		dateFormat: 'mm-dd-yyyy',
		timezones: [
			{
				id: 'Africa/Johannesburg',
				timezone: asLabel('time-zone-list.africa-johannesburg')
			}
		]
	},
	{
		id: 'Korea, Republic of',
		isoCode: 'KR',
		label: asLabel('countries-list.south-korea'),
		dateFormat: 'mm/dd/yyyy',
		timezones: [
			{
				id: 'Asia/Seoul',
				timezone: asLabel('time-zone-list.asia-seoul')
			}
		]
	},
	{
		id: 'Sudan',
		isoCode: 'SD',
		label: asLabel('countries-list.sudan'),
		timezones: [
			{
				id: 'Africa/Khartoum',
				timezone: asLabel('time-zone-list.africa-khartoum')
			}
		]
	},
	{
		id: 'Spain',
		isoCode: 'ES',
		label: asLabel('countries-list.spain'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Africa/Ceuta',
				timezone: asLabel('time-zone-list.africa-ceuta')
			},
			{
				id: 'Atlantic/Canary',
				timezone: asLabel('time-zone-list.atlantic-canary')
			},
			{
				id: 'Europe/Madrid',
				timezone: asLabel('time-zone-list.europe-madrid')
			}
		]
	},
	{
		id: 'Sri Lanka',
		isoCode: 'LK',
		label: asLabel('countries-list.sri-lanka'),
		dateFormat: 'dd-mm-yyyy',
		timezones: [
			{
				id: 'Asia/Colombo',
				timezone: asLabel('time-zone-list.asia-colombo')
			}
		]
	},
	{
		id: 'Sweden',
		isoCode: 'SE',
		label: asLabel('countries-list.sweden'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Berlin',
				timezone: asLabel('time-zone-list.europe-berlin')
			}
		]
	},
	{
		id: 'Switzerland',
		isoCode: 'CH',
		label: asLabel('countries-list.switzerland'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Zurich',
				timezone: asLabel('time-zone-list.europe-zurich')
			}
		]
	},
	{
		id: 'Tanzania, United Republic of',
		isoCode: 'TZ',
		label: asLabel('countries-list.tanzania'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Africa/Nairobi',
				timezone: asLabel('time-zone-list.africa-nairobi')
			}
		]
	},
	{
		id: 'Thailand',
		isoCode: 'TH',
		label: asLabel('countries-list.thailand'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Bangkok',
				timezone: asLabel('time-zone-list.asia-bangkok')
			}
		]
	},
	{
		id: 'Trinidad and Tobago',
		isoCode: 'TT',
		label: asLabel('countries-list.trinidad-and-tobago'),
		timezones: [
			{
				id: 'America/Puerto_Rico',
				timezone: asLabel('time-zone-list.america-puerto-rico')
			}
		]
	},
	{
		id: 'Tunisia',
		isoCode: 'TN',
		label: asLabel('countries-list.tunisia'),
		timezones: [
			{
				id: 'Africa/Tunis',
				timezone: asLabel('time-zone-list.africa-tunis')
			}
		]
	},
	{
		id: 'Turkey',
		isoCode: 'TR',
		label: asLabel('countries-list.turkey'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Europe/Istanbul',
				timezone: asLabel('time-zone-list.europe-istanbul')
			}
		]
	},
	{
		id: 'U.A.E.',
		isoCode: 'AE',
		label: asLabel('countries-list.united-arab-emirates'),
		timezones: [
			{
				id: 'Asia/Dubai',
				timezone: asLabel('time-zone-list.asia-dubai')
			}
		]
	},
	{
		id: 'American Virgin Islands',
		isoCode: 'VI',
		label: asLabel('countries-list.u.s.-virgin-islands'),
		timezones: [
			{
				id: 'America/Puerto_Rico',
				timezone: asLabel('time-zone-list.america-puerto-rico')
			}
		]
	},
	{
		id: 'Uganda',
		isoCode: 'UG',
		label: asLabel('countries-list.uganda'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Africa/Nairobi',
				timezone: asLabel('time-zone-list.africa-nairobi')
			}
		]
	},
	{
		id: 'United Kingdom',
		isoCode: 'GB',
		label: asLabel('countries-list.united-kingdom'),
		dateFormat: 'mm-dd-yyyy',
		timezones: [
			{
				id: 'Europe/London',
				timezone: asLabel('time-zone-list.europe-london')
			}
		]
	},
	{
		id: 'United States',
		isoCode: 'US',
		label: asLabel('countries-list.united-states'),
		dateFormat: 'mm-dd-yyyy',
		timezones: [
			{
				id: 'America/Adak',
				timezone: asLabel('time-zone-list.america-adak')
			},
			{
				id: 'America/Anchorage',
				timezone: asLabel('time-zone-list.america-anchorage')
			},
			{
				id: 'America/Boise',
				timezone: asLabel('time-zone-list.america-boise')
			},
			{
				id: 'America/Chicago',
				timezone: asLabel('time-zone-list.america-chicago')
			},
			{
				id: 'America/Denver',
				timezone: asLabel('time-zone-list.america-denver')
			},
			{
				id: 'America/Detroit',
				timezone: asLabel('time-zone-list.america-detroit')
			},
			{
				id: 'America/Indiana/Indianapolis',
				timezone: asLabel('time-zone-list.america-indiana-indianapolis')
			},
			{
				id: 'America/Indiana/Knox',
				timezone: asLabel('time-zone-list.america-indiana-knox')
			},
			{
				id: 'America/Indiana/Marengo',
				timezone: asLabel('time-zone-list.america-indiana-marengo')
			},
			{
				id: 'America/Indiana/Petersburg',
				timezone: asLabel('time-zone-list.america-indiana-petersburg')
			},
			{
				id: 'America/Indiana/Tell_City',
				timezone: asLabel('time-zone-list.america-indiana-tell-city')
			},
			{
				id: 'America/Indiana/Vevay',
				timezone: asLabel('time-zone-list.america-indiana-vevay')
			},
			{
				id: 'America/Indiana/Vincennes',
				timezone: asLabel('time-zone-list.america-indiana-vincennes')
			},
			{
				id: 'America/Indiana/Winamac',
				timezone: asLabel('time-zone-list.america-indiana-winamac')
			},
			{
				id: 'America/Juneau',
				timezone: asLabel('time-zone-list.america-juneau')
			},
			{
				id: 'America/Kentucky/Louisville',
				timezone: asLabel('time-zone-list.america-kentucky-louisville')
			},
			{
				id: 'America/Kentucky/Monticello',
				timezone: asLabel('time-zone-list.america-kentucky-monticello')
			},
			{
				id: 'America/Los_Angeles',
				timezone: asLabel('time-zone-list.america-los-angeles')
			},
			{
				id: 'America/Menominee',
				timezone: asLabel('time-zone-list.america-menominee')
			},
			{
				id: 'America/Metlakatla',
				timezone: asLabel('time-zone-list.america-metlakatla')
			},
			{
				id: 'America/New_York',
				timezone: asLabel('time-zone-list.america-new-york')
			},
			{
				id: 'America/Nome',
				timezone: asLabel('time-zone-list.america-nome')
			},
			{
				id: 'America/North_Dakota/Beulah',
				timezone: asLabel('time-zone-list.america-north-dakota-beulah')
			},
			{
				id: 'America/North_Dakota/Center',
				timezone: asLabel('time-zone-list.america-north-dakota-center')
			},
			{
				id: 'America/North_Dakota/New_Salem',
				timezone: asLabel('time-zone-list.america-north-dakota-new-salem')
			},
			{
				id: 'America/Phoenix',
				timezone: asLabel('time-zone-list.america-phoenix')
			},
			{
				id: 'America/Sitka',
				timezone: asLabel('time-zone-list.america-sitka')
			},
			{
				id: 'America/Yakutat',
				timezone: asLabel('time-zone-list.america-yakutat')
			},
			{
				id: 'Pacific/Honolulu',
				timezone: asLabel('time-zone-list.pacific-honolulu')
			}
		]
	},
	{
		id: 'Uruguay',
		isoCode: 'UY',
		label: asLabel('countries-list.uruguay'),
		timezones: [
			{
				id: 'America/Montevideo',
				timezone: asLabel('time-zone-list.america-montevideo')
			}
		]
	},
	{
		id: 'Venezuela, Bolivarian Republic of',
		isoCode: 'VE',
		label: asLabel('countries-list.venezuela'),
		timezones: [
			{
				id: 'America/Caracas',
				timezone: asLabel('time-zone-list.america-caracas')
			}
		]
	},
	{
		id: 'Viet Nam',
		isoCode: 'VN',
		label: asLabel('countries-list.vietnam'),
		dateFormat: 'dd/mm/yyyy',
		timezones: [
			{
				id: 'Asia/Bangkok',
				timezone: asLabel('time-zone-list.asia-bangkok')
			},
			{
				id: 'Asia/Ho_Chi_Minh',
				timezone: asLabel('time-zone-list.asia-ho-chi-minh')
			}
		]
	},
	{
		id: 'Zambia',
		isoCode: 'ZM',
		label: asLabel('countries-list.zambia'),
		timezones: [
			{
				id: 'Africa/Maputo',
				timezone: asLabel('time-zone-list.africa-maputo')
			}
		]
	},
	{
		id: 'Zimbabwe',
		isoCode: 'ZW',
		label: asLabel('countries-list.zimbabwe'),
		timezones: [
			{
				id: 'Africa/Maputo',
				timezone: asLabel('time-zone-list.africa-maputo')
			}
		]
	}
];
